import React, { useRef, useState, useCallback, useEffect, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonPopover,
  IonList,
  IonItem,
  IonLabel,
  IonRadioGroup,
  IonRadio,
  IonText,
  IonChip,
  IonBadge,
  IonAlert,
} from '@ionic/react';
import {
  chevronBackOutline,
  chevronForwardOutline,
  chevronDownOutline,
  calendarOutline,
  listOutline,
  todayOutline,
  gridOutline,
  closeOutline,
  timeOutline,
  personOutline,
  alertCircleOutline,
  createOutline,
  trashOutline,
  checkmark,
} from 'ionicons/icons';
import { format, parse, addDays, addWeeks, addMonths, startOfWeek, startOfMonth, endOfMonth, endOfWeek, isSameDay, isSameMonth, startOfDay } from 'date-fns';
import { BookingItem, Booking as BookingRecord, DayBookings } from '@rsmaids/shared/types/bookings';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { subscribeToBlockedSlots, subscribeToUpcomingBookings } from '@rsmaids/shared/services/api/bookingsCacheService';
import { formatTo12Hour } from '@rsmaids/shared/utils/timeUtils';
import MiniCalendarPicker from '../../../components/MiniCalendarPicker';
import GcalTimePicker from '../../../components/GcalTimePicker';
import Booking from '../../../pages/bookingSystem/BookingForm';
import CreateBookingDesktop from '../bookings/CreateBookingDesktop';
import BookingDetailsDesktop from '../bookings/BookingDetailsDesktop';
import PdfViewerNative from '../../../components/PdfViewerNative';
import TodayCalendarIcon from '../../../components/TodayCalendarIcon';
import { getFunctions, httpsCallable } from 'firebase/functions';
import styles from './CalendarDesktop.module.scss';
import bookingDetailsStyles from '../bookings/BookingDetailsDesktop.module.scss';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import { useAuth } from '../../../contexts';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { translateDynamic } from '../../../i18n/dynamic';
import { dfLocale, weekdayNames } from '../../../i18n/locale';

type CalendarView = 'weekly' | 'daily' | 'month' | 'schedule';

interface BlockedSlot {
  type: 'blocked';
  agendaItem?: {
    title?: string;
  };
}

type ExtendedBookingItem = BookingItem | (BookingItem & BlockedSlot);

type BlockedEditDraft = {
  eventId: string;
  revision?: string;
  scheduleId?: string;
  staffId?: string;
  availability: 'BUSY' | 'AVAILABLE';
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  notes: string;
  repeat: boolean;
  repeatInterval: number;
  repeatUntil: string;
};

type StaffOption = { id: string; name: string; scheduleId: string; email: string };

// Wix's calendar recurrenceRule only supports WEEKLY frequency, on the same
// weekday as the event's own start date (days accepts exactly one entry).
const WIX_WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;
const getWixWeekday = (isoDate: string): string => {
  const parsed = new Date(`${isoDate}T00:00:00`);
  return WIX_WEEKDAYS[parsed.getDay()] || 'MONDAY';
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (hour: number): string => {
  if (hour === 0) return i18n.t('desktop.pages.calendar.CalendarDesktop.12Am');
  if (hour < 12) return i18n.t('desktop.pages.calendar.CalendarDesktop.am', { hour });
  if (hour === 12) return i18n.t('desktop.pages.calendar.CalendarDesktop.12Pm');
  return i18n.t('desktop.pages.calendar.CalendarDesktop.pm', { v1: hour - 12 });
};

const formatTimeToString = (time: string | Date): string => {
  if (typeof time === 'string') return time;
  return time.toTimeString().substring(0, 5);
};

const toDate = (val: string | Date): Date => {
  if (val instanceof Date) return val;
  return new Date(val);
};

const getTimePosition = (time: string | Date): number => {
  const d = toDate(time);
  return (d.getHours() * 60 + d.getMinutes()) * (60 / 60); // 1px per minute
};

const getBookingHeight = (startTime: string | Date, endTime: string | Date): number => {
  const start = toDate(startTime);
  const end = toDate(endTime);
  const minutes = (end.getTime() - start.getTime()) / 60000;
  return Math.max(minutes, 15); // minimum 15px
};

const normalizeTimeInput = (input: string, fallback = '09:00'): string => {
  const raw = (input || '').trim();
  if (!raw) return fallback;

  const hhmmssMatch = raw.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (hhmmssMatch) {
    const h = Number(hhmmssMatch[1]);
    const m = Number(hhmmssMatch[2]);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }

  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (ampmMatch) {
    let h = Number(ampmMatch[1]);
    const m = Number(ampmMatch[2]);
    const ampm = ampmMatch[3].toUpperCase();
    if (m < 0 || m > 59 || h < 1 || h > 12) return fallback;
    if (ampm === 'AM') {
      h = h === 12 ? 0 : h;
    } else {
      h = h === 12 ? 12 : h + 12;
    }
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  return fallback;
};

const normalizeDateInput = (input: string, fallback = format(new Date(), 'yyyy-MM-dd')): string => {
  const raw = (input || '').trim();
  const isoDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) return raw;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return format(parsed, 'yyyy-MM-dd');
  }

  return fallback;
};

const buildLocalDateTime = (isoDate: string, time: string, fallbackTime: string): string => {
  const safeDate = normalizeDateInput(isoDate);
  const safeTime = normalizeTimeInput(time, fallbackTime);
  const strict = `${safeDate}T${safeTime}:00`;
  const strictMatch = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(strict);
  if (strictMatch) return strict;

  const safeFallbackTime = normalizeTimeInput(fallbackTime, '09:00');
  return `${safeDate}T${safeFallbackTime}:00`;
};

const resolveBlockedEventId = (booking: BookingItem): string | null => {
  const anyBooking = booking as unknown as Record<string, unknown>;
  const agenda = (anyBooking.agendaItem as Record<string, unknown> | undefined) || undefined;

  const candidates = [
    agenda?._id,
    agenda?.eventId,
    anyBooking.eventId,
    anyBooking.calendarEventId,
  ].map((v) => (typeof v === 'string' ? v.trim() : ''));

  const first = candidates.find(Boolean);
  if (first) return first;

  if (typeof booking.id === 'string' && booking.id.startsWith('blocked-')) {
    const stripped = booking.id.replace(/^blocked-/, '').trim();
    return stripped || null;
  }

  return null;
};

// Mini calendar component for left sidebar
const MiniCalendar: React.FC<{
  currentMonth: Date;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  bookingsByDate?: Map<string, BookingItem[]>;
}> = ({ currentMonth, selectedDate, onDateSelect, onMonthChange, bookingsByDate }) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);

  const days: Date[] = [];
  let d = calStart;
  while (d <= calEnd) {
    days.push(d);
    d = addDays(d, 1);
  }

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className={styles.miniCalendar}>
      <div className={styles.miniCalendarHeader}>
        <IonButton fill="clear" size="small" onClick={() => onMonthChange(addMonths(currentMonth, -1))}>
          <IonIcon icon={chevronBackOutline} />
        </IonButton>
        <span className={styles.miniCalendarTitle}>
          {format(currentMonth, 'MMMM yyyy', { locale: dfLocale() })}
        </span>
        <IonButton fill="clear" size="small" onClick={() => onMonthChange(addMonths(currentMonth, 1))}>
          <IonIcon icon={chevronForwardOutline} />
        </IonButton>
      </div>
      <table className={styles.miniCalendarGrid}>
        <thead>
          <tr>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <th key={day}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((day) => {
                const isToday = isSameDay(day, new Date());
                const isSelected = isSameDay(day, selectedDate);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const dateKey = format(day, 'yyyy-MM-dd');
                const dayBookings = bookingsByDate?.get(dateKey) || [];

                // Get unique service types for this day
                const serviceTypes = [...new Set(dayBookings.map(b => b.serviceType))].slice(0, 3);

                const dotCount = serviceTypes.length;
                const color1 = serviceColorMap[serviceTypes[0] as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';
                const color2 = serviceTypes[1] ? (serviceColorMap[serviceTypes[1] as keyof typeof serviceColorMap] || 'var(--ion-color-primary)') : null;
                const color3 = serviceTypes[2] ? (serviceColorMap[serviceTypes[2] as keyof typeof serviceColorMap] || 'var(--ion-color-primary)') : null;
                const shadowParts = [
                  color2 ? `6px 0 0 0 ${color2}` : null,
                  color3 ? `12px 0 0 0 ${color3}` : null,
                ].filter(Boolean).join(', ');
                // Offset so the whole group is centered: 1 dot=0, 2 dots=-3px, 3 dots=-6px
                const dotGroupOffset = dotCount === 3 ? '-6px' : dotCount === 2 ? '-3px' : '0px';

                return (
                  <td
                    key={day.getTime()}
                    className={`${styles.miniCalendarDay}
                      ${isToday ? styles.today : ''}
                      ${isSelected ? styles.selected : ''}
                      ${!isCurrentMonth ? styles.otherMonth : ''}
                      ${dotCount > 0 ? styles.hasBookings : ''}`}
                    style={dotCount > 0 ? {
                      '--dot-color-1': color1,
                      '--dot-shadows': shadowParts || 'none',
                      '--dot-offset': dotGroupOffset,
                    } as React.CSSProperties : undefined}
                    onClick={() => onDateSelect(day)}
                  >
                    <span className={styles.dateNumber}>{day.getDate()}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Booking card for time grid
const BookingCard: React.FC<{
  booking: ExtendedBookingItem;
  onSelect: (booking: BookingItem) => void;
  renderKey: string;
  columnIndex?: number;
  totalColumns?: number;
  sortIndex?: number;
  onPointerDragStart?: (payload: { booking: BookingItem; renderKey: string; sourceElement: HTMLDivElement; grabOffsetMinutes: number; x: number; y: number; width: number; height: number; offsetX: number; offsetY: number; title: string; subtitle: string; color: string }) => void;
  isDragging?: boolean;
  floatingStyle?: React.CSSProperties;
}> = memo(({ booking, onSelect, renderKey, columnIndex = 0, totalColumns = 1, sortIndex, onPointerDragStart, isDragging, floatingStyle }) => {
  const { t: tr } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const top = getTimePosition(booking.startTime);
  const height = getBookingHeight(booking.startTime, booking.endTime);
  const serviceColor = serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';

  const isBlocked = 'type' in booking && booking.type === 'blocked';
  // Wix-style stagger: each overlapping card is 75% wide, offset by 25%/(n-1) per column.
  // Cards overlap each other; later columns render on top. Non-overlapping = full width.
  const CARD_W = 0.75;
  const staggerStep = totalColumns > 1 ? (1 - CARD_W) / Math.max(1, totalColumns - 1) : 0;
  const width = totalColumns > 1 ? `calc(${CARD_W * 100}% - 2px)` : 'calc(100% - 8px)';
  const left = totalColumns > 1 ? `calc(${columnIndex * staggerStep * 100}% + 2px)` : '4px';

  const blockedTitle = ('agendaItem' in booking && booking.agendaItem?.title) || tr('desktop.pages.calendar.CalendarDesktop.blocked');
  const contactName = isBlocked ? blockedTitle : (booking.contact?.name || booking.title || 'Unnamed');
  const resourceInfo = booking.resource;
  const cardColor = isBlocked ? 'var(--ion-color-medium)' : serviceColor;

  return (
    <div
      className={`${styles.bookingCardWrapper} ${isDragging ? styles.bookingCardDragging : ''}`}
      style={{
        position: 'absolute',
        top: `${top}px`,
        height: `${height}px`,
        left,
        width,
        zIndex: isDragging ? 1 : isHovered ? 99 : 3 + (sortIndex ?? columnIndex),
        '--booking-color': cardColor,
        ...(isDragging ? floatingStyle : null),
      } as React.CSSProperties}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        // Prevent empty-slot click handler from arming while starting a drag on a booking.
        e.stopPropagation();
        const dragElement = e.currentTarget as HTMLDivElement;
        const rect = dragElement.getBoundingClientRect();
        const grabOffsetMinutes = Math.max(0, Math.floor(e.clientY - rect.top));
        const offsetX = Math.max(0, e.clientX - rect.left);
        const offsetY = Math.max(0, e.clientY - rect.top);

        onPointerDragStart?.({
          booking: booking as BookingItem,
          renderKey,
          sourceElement: dragElement,
          grabOffsetMinutes,
          x: e.clientX,
          y: e.clientY,
          width: rect.width,
          height: rect.height,
          offsetX,
          offsetY,
          title: contactName,
          subtitle: isBlocked ? translateDynamic('Blocked Time') : translateDynamic(booking.serviceType),
          color: cardColor,
        });
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(booking as BookingItem);
      }}
    >
      <div className={`${styles.bookingCard} ${isBlocked ? styles.blockedCard : ''}`}>
        <div className={styles.bookingCardContent}>
          <span className={styles.bookingContactName}>{contactName}</span>
          {!isBlocked && <span className={styles.bookingServiceType}>{translateDynamic(booking.serviceType)}</span>}
          {!isBlocked && resourceInfo && (
            <span className={styles.bookingResource}>{tr('desktop.pages.calendar.CalendarDesktop.with', { resourceInfo })}</span>
          )}
          {isBlocked && booking.staff && (
            <span className={styles.bookingResource}>{booking.staff}</span>
          )}
        </div>
      </div>
    </div>
  );
});

// Schedule view item
const ScheduleItem: React.FC<{
  booking: BookingItem;
  onSelect: (booking: BookingItem) => void;
}> = ({ booking, onSelect }) => {
  const { t: tr } = useTranslation();
  const serviceColor = serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';
  const contactName = booking.contact?.name || booking.title || 'Unnamed';
  const startStr = formatTo12Hour(formatTimeToString(booking.startTime));
  const endStr = formatTo12Hour(formatTimeToString(booking.endTime));
  const resourceInfo = booking.resource;

  return (
    <IonItem
      className={styles.scheduleItem}
      button
      detail={false}
      lines="none"
      onClick={() => onSelect(booking)}
    >
      <IonBadge slot="start" className={styles.scheduleItemColor} style={{ '--service-color': serviceColor } as React.CSSProperties}>&nbsp;</IonBadge>
      <IonLabel>
        <div className={styles.scheduleItemContent}>
          <div className={styles.scheduleItemTime}>
            <span>{startStr}</span>
            <span className={styles.scheduleItemTimeSep}>-</span>
            <span>{endStr}</span>
          </div>
          <div className={styles.scheduleItemDetails}>
            <span className={styles.scheduleItemContact}>{contactName}</span>
            <span className={styles.scheduleItemTitle}>{translateDynamic(booking.serviceType)}</span>
            {resourceInfo && <span className={styles.scheduleItemResource}>{tr('desktop.pages.calendar.CalendarDesktop.with', { resourceInfo })}</span>}
          </div>
        </div>
      </IonLabel>
    </IonItem>
  );
};

const CalendarDesktop: React.FC = () => {
  const { t: tr } = useTranslation();
  console.log('[CalendarDesktop] mounting');
  const location = useLocation();
  const { authState } = useAuth();
  const currentUserEmail = authState.user?.email?.toLowerCase();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>(() => {
    return (localStorage.getItem('desktopCalendarView') as CalendarView) || 'weekly';
  });
  const [showViewPopover, setShowViewPopover] = useState(false);
  const [showAddPopover, setShowAddPopover] = useState(false);
  const [expandedMonthCells, setExpandedMonthCells] = useState<Set<string>>(new Set());
  const [miniCalMonth, setMiniCalMonth] = useState(new Date());
  const [bookingsByDay, setBookingsByDay] = useState<DayBookings[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [isSidebarEditing, setIsSidebarEditing] = useState(false);
  const [forceWixUntil, setForceWixUntil] = useState<number>(() => {
    const raw = sessionStorage.getItem('desktopCalendarForceWixUntil');
    return raw ? Number(raw) || 0 : 0;
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [slotMenu, setSlotMenu] = useState<{ x: number; y: number; date: Date; hasTime: boolean; side: 'right' | 'left' } | null>(null);
  const [dragVisual, setDragVisual] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    title: string;
    subtitle: string;
    color: string;
  } | null>(null);

  const [calSelectedBookingId, setCalSelectedBookingId] = useState<string | null>(null);
  const [calSelectedBlocked, setCalSelectedBlocked] = useState<BookingItem | null>(null);
  const [blockedActionBusy, setBlockedActionBusy] = useState(false);
  const [showDeleteBlockedConfirm, setShowDeleteBlockedConfirm] = useState(false);
  const [blockedEditDraft, setBlockedEditDraft] = useState<BlockedEditDraft | null>(null);
  const [blockedEditBusy, setBlockedEditBusy] = useState(false);
  const [blockedEditPicker, setBlockedEditPicker] = useState<'date' | 'start' | 'end' | 'staff' | 'availability' | 'repeatInterval' | 'repeatUntil' | null>(null);
  const blockedEditFieldsRef = useRef<HTMLDivElement | null>(null);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [staffOptionsLoading, setStaffOptionsLoading] = useState(true);
  const [calPdfPanel, setCalPdfPanel] = useState<'invoice' | 'so' | null>(null);
  const [calPdfUrl, setCalPdfUrl] = useState<string | null>(null);
  const [calPdfLoading, setCalPdfLoading] = useState(false);
  const [calPdfTitle, setCalPdfTitle] = useState('');

  const timeGridRef = useRef<HTMLDivElement>(null);
  const dragStartPointerRef = useRef<{ x: number; y: number } | null>(null);
  const activeDragRef = useRef<{
    booking: BookingItem;
    renderKey: string;
    sourceElement: HTMLDivElement | null;
    grabOffsetMinutes: number;
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    title: string;
    subtitle: string;
    color: string;
  } | null>(null);
  const pointerMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const pointerUpHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const dragCommittedRef = useRef(false);
  // Render-visible snapshot of activeDragRef, set only at the moment
  // dragCommittedRef flips true (not on every mousemove) so the dragged
  // booking's original slot can be suppressed from the grid while the drag
  // overlay shows it instead. activeDragRef/dragCommittedRef stay refs for
  // the imperative 60fps mousemove logic; this is the one state update per
  // drag that the render path actually needs.
  const [committedDrag, setCommittedDrag] = useState<{ renderKey: string; booking: BookingItem } | null>(null);

  // Current time indicator
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Staff list for the "Block off time" Staff picker — loaded once, matching
  // Wix's native panel which lets you pick which staff member's schedule a
  // block goes on instead of always falling back to a generic business schedule.
  // staff.scheduleIds is often empty on this query — same as
  // fetchFallbackScheduleId below, cross-reference querySchedules by resourceId
  // to reliably resolve each staff member's schedule.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use the same proven staff-fetch path as CreateBookingDesktop
        // (svc.staff.getAllStaffMembers) rather than calling queryStaffMembers
        // directly — that raw call's response shape wasn't reliably parsed here.
        const service = DataServiceFactory.getService();
        const [staffList, schedulesResult] = await Promise.all([
          service.staff.getAllStaffMembers(),
          callAdminApi('querySchedules', 'bookings', {}),
        ]);

        const anySchedules = schedulesResult as Record<string, unknown>;
        const scheduleList = (Array.isArray(anySchedules.items) ? anySchedules.items : Array.isArray(anySchedules._items) ? anySchedules._items : []) as Array<Record<string, unknown>>;
        const scheduleIdByResourceId = new Map<string, string>();
        scheduleList.forEach((schedule) => {
          const scheduleId = typeof schedule._id === 'string' ? schedule._id.trim() : (typeof schedule.id === 'string' ? schedule.id.trim() : '');
          // Schedule has no `resourceId` field — for a staff member's own
          // schedule, `externalId` is what equals that staff's resourceId.
          // Matching on the (nonexistent) `resourceId` silently failed for
          // every staff member, which is why blocked time always fell through
          // to the generic business-schedule fallback regardless of who was
          // selected in the Staff picker.
          const resourceId = typeof schedule.externalId === 'string' ? schedule.externalId.trim() : '';
          if (scheduleId && resourceId) scheduleIdByResourceId.set(resourceId, scheduleId);
        });

        const options: StaffOption[] = staffList
          .map((staff) => {
            const staffRecord = staff as unknown as Record<string, unknown>;
            const id = typeof staff._id === 'string' ? staff._id.trim() : '';
            const name = typeof staff.name === 'string' ? staff.name : '';
            const email = typeof staff.email === 'string' ? staff.email.trim() : '';
            const resourceId = typeof staff.resourceId === 'string' ? staff.resourceId.trim() : '';
            const scheduleIds = Array.isArray(staffRecord.scheduleIds) ? staffRecord.scheduleIds : [];
            const firstStaffSchedule = scheduleIds.find((sid): sid is string => typeof sid === 'string' && sid.trim().length > 0) || '';
            const scheduleId = firstStaffSchedule
              || scheduleIdByResourceId.get(resourceId)
              || scheduleIdByResourceId.get(id)
              || '';
            return { id, name, scheduleId, email };
          })
          .filter((option) => option.id && option.name);
        console.log('[CalendarDesktop] Staff options loaded:', {
          staffCount: staffList.length,
          scheduleCount: scheduleList.length,
          resolvedCount: options.length,
          sampleStaff: staffList[0],
          sampleSchedule: scheduleList[0],
        });
        if (!cancelled) setStaffOptions(options);
      } catch (err) {
        console.error('[CalendarDesktop] Failed to load staff options:', err);
      } finally {
        if (!cancelled) setStaffOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!blockedEditPicker) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const root = blockedEditFieldsRef.current;
      const target = event.target as Node | null;
      if (!root || !target) return;
      if (!root.contains(target)) {
        setBlockedEditPicker(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setBlockedEditPicker(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [blockedEditPicker]);

  // Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as a setState call inside an effect —
  // this only re-evaluates when forceWixUntil itself changes, so it's a
  // pure derived check, not a live ticking clock.
  if (forceWixUntil > 0 && new Date().getTime() >= forceWixUntil) {
    setForceWixUntil(0);
    sessionStorage.removeItem('desktopCalendarForceWixUntil');
  }

  const currentTimePosition = useMemo(() => {
    return currentTime.getHours() * 60 + currentTime.getMinutes();
  }, [currentTime]);

  // Scroll to current time on mount/view-change — intentionally reads the
  // current position at that moment via a ref rather than depending on
  // currentTimePosition directly, which ticks every 60s and would otherwise
  // re-trigger this scroll-into-view on every tick.
  const currentTimePositionRef = useRef(currentTimePosition);
  useEffect(() => {
    currentTimePositionRef.current = currentTimePosition;
  });
  useEffect(() => {
    if (timeGridRef.current) {
      const scrollTo = Math.max(0, currentTimePositionRef.current - 200);
      timeGridRef.current.scrollTop = scrollTo;
    }
  }, [calendarView]);

  // Reset expanded cells when month, view, or page changes — computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as a setState call inside an effect.
  const expandedCellsResetKey = `${format(currentDate, 'yyyy-MM')}|${calendarView}|${location.pathname}`;
  const [prevExpandedCellsResetKey, setPrevExpandedCellsResetKey] = useState(expandedCellsResetKey);
  if (expandedCellsResetKey !== prevExpandedCellsResetKey) {
    setPrevExpandedCellsResetKey(expandedCellsResetKey);
    setExpandedMonthCells(new Set());
  }

  // Calculate visible date range based on view
  const { viewStart, viewEnd, headerDates } = useMemo(() => {
    let start: Date, end: Date;
    switch (calendarView) {
      case 'weekly': {
        start = startOfWeek(currentDate);
        end = addDays(start, 6);
        break;
      }
      case 'daily': {
        start = startOfDay(currentDate);
        end = start;
        break;
      }
      case 'month': {
        start = startOfWeek(startOfMonth(currentDate));
        end = endOfWeek(endOfMonth(currentDate));
        break;
      }
      case 'schedule': {
        start = startOfDay(currentDate);
        end = addDays(start, 13); // 2 weeks of schedule
        break;
      }
      default: {
        start = startOfWeek(currentDate);
        end = addDays(start, 6);
      }
    }

    const dates: Date[] = [];
    let d = start;
    while (d <= end) {
      dates.push(d);
      d = addDays(d, 1);
    }

    return { viewStart: start, viewEnd: end, headerDates: dates };
  }, [currentDate, calendarView]);

  function groupBookingsByDay(bookings: BookingItem[], start: Date, end: Date): DayBookings[] {
    const days: DayBookings[] = [];
    let d = new Date(start);
    while (d <= end) {
      const dayStart = startOfDay(d);
      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);
      const items = bookings.filter(b => {
        const bStart = toDate(b.startTime);
        return bStart >= dayStart && bStart <= dayEnd;
      });
      days.push({ date: new Date(dayStart), items });
      d = addDays(d, 1);
    }
    return days;
  }

  // Subscribe to bookings — Firestore real-time snapshot within ±45 days, Wix API otherwise
  useEffect(() => {
    const windowStart = new Date(viewStart);
    windowStart.setHours(0, 0, 0, 0);
    const windowEnd = new Date(viewEnd);
    windowEnd.setHours(23, 59, 59, 999);

    const forceWixActive = Date.now() < forceWixUntil;
    const now = new Date();
    // Past bounded at 90 days, future effectively unbounded (10 years) — matches
    // onWixBookingWebhook.ts's isInWindow and Calendar.tsx's mobile equivalent.
    const fsStart = new Date(now); fsStart.setDate(now.getDate() - 90);
    const fsEnd = new Date(now); fsEnd.setDate(now.getDate() + 3650);
    const withinFirestoreWindow = !forceWixActive && windowStart >= fsStart && windowEnd <= fsEnd;

    const loadServiceIds = async (service: { getServiceTypes: () => Promise<Array<{ serviceId?: string }>> }): Promise<string[]> => {
      const cachedServiceIds = sessionStorage.getItem('cachedServiceIds');
      if (cachedServiceIds) {
        return JSON.parse(cachedServiceIds) as string[];
      }

      const services = await service.getServiceTypes();
      const serviceIds = services
        .map((s: { serviceId?: string }) => s.serviceId)
        .filter((id: string | undefined): id is string => Boolean(id));
      sessionStorage.setItem('cachedServiceIds', JSON.stringify(serviceIds));
      return serviceIds;
    };

    if (withinFirestoreWindow) {
      // Deferred via a resolved-promise microtask (not called synchronously
      // in the effect body) — the listener setup right below is unaffected
      // since it doesn't itself call setState synchronously at its top.
      Promise.resolve().then(() => setIsLoading(true));
      let cancelled = false;
      let latestBookings: BookingItem[] = [];
      let latestBlocked: BookingItem[] = [];
      let bookingsReady = false;
      let blockedReady = false;

      const publishMergedCache = () => {
        if (cancelled) return;

        const seen = new Set<string>();
        const merged: BookingItem[] = [];
        [...latestBookings, ...latestBlocked].forEach((item) => {
          if (seen.has(item.id)) return;
          seen.add(item.id);
          merged.push(item);
        });

        setBookingsByDay(groupBookingsByDay(merged, windowStart, windowEnd));

        if (bookingsReady && blockedReady) {
          setIsLoading(false);
        }
      };

      const unsubscribeBookings = subscribeToUpcomingBookings(
        windowStart,
        windowEnd,
        (bookings) => {
          latestBookings = bookings;
          bookingsReady = true;
          publishMergedCache();
        },
        () => {
          bookingsReady = true;
          publishMergedCache();
        }
      );

      const unsubscribeBlocked = subscribeToBlockedSlots(
        windowStart,
        windowEnd,
        (blocked) => {
          latestBlocked = blocked;
          blockedReady = true;
          publishMergedCache();
        },
        () => {
          blockedReady = true;
          publishMergedCache();
        }
      );

      return () => {
        cancelled = true;
        unsubscribeBookings();
        unsubscribeBlocked();
      };
    }

    // Wix API fallback (outside ±45-day window or force-Wix mode active after a drag-drop)
    let cancelled = false;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body).
    Promise.resolve().then(() => setIsLoading(true));
    (async () => {
      try {
        const service = DataServiceFactory.getService();
        const serviceIds = await loadServiceIds(service);
        const bookings = await service.bookings.getBookingsAndBlockedSlotsByDateRange(windowStart, windowEnd, serviceIds);
        if (!cancelled) setBookingsByDay(groupBookingsByDay(bookings, windowStart, windowEnd));
      } catch {
        if (!cancelled) setBookingsByDay([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewStart, viewEnd, forceWixUntil, refreshTrigger]);

  // Get bookings for a specific date
  const getBookingsForDate = useCallback((date: Date): BookingItem[] => {
    const day = bookingsByDay.find(d => isSameDay(d.date, date));
    return (day?.items || []).filter(b => {
      const status = String(b.status).toLowerCase();
      return status !== 'canceled' && status !== 'cancelled';
    });
  }, [bookingsByDay]);

  // Booking dates for mini calendar dots (with service colors)
  const bookingsByDate = useMemo(() => {
    const dateMap = new Map<string, BookingItem[]>();
    bookingsByDay.forEach(day => {
      if (day.items.length > 0) {
        const dateKey = format(day.date, 'yyyy-MM-dd');
        const filteredBookings = day.items.filter(b => {
          const status = String(b.status).toLowerCase();
          return status !== 'canceled' && status !== 'cancelled';
        });
        if (filteredBookings.length > 0) {
          dateMap.set(dateKey, filteredBookings);
        }
      }
    });
    return dateMap;
  }, [bookingsByDay]);

  // Navigation
  const navigatePrev = useCallback(() => {
    switch (calendarView) {
      case 'weekly':
        setCurrentDate(d => addWeeks(d, -1));
        break;
      case 'daily':
        setCurrentDate(d => addDays(d, -1));
        break;
      case 'month':
        setCurrentDate(d => addMonths(d, -1));
        setMiniCalMonth(d => addMonths(d, -1));
        break;
      case 'schedule':
        setCurrentDate(d => addWeeks(d, -2));
        break;
    }
  }, [calendarView]);

  const navigateNext = useCallback(() => {
    switch (calendarView) {
      case 'weekly':
        setCurrentDate(d => addWeeks(d, 1));
        break;
      case 'daily':
        setCurrentDate(d => addDays(d, 1));
        break;
      case 'month':
        setCurrentDate(d => addMonths(d, 1));
        setMiniCalMonth(d => addMonths(d, 1));
        break;
      case 'schedule':
        setCurrentDate(d => addWeeks(d, 2));
        break;
    }
  }, [calendarView]);

  const goToToday = useCallback(() => {
    const now = new Date();
    setCurrentDate(now);
    setMiniCalMonth(now);
  }, []);

  const handleViewChange = useCallback((view: CalendarView) => {
    setCalendarView(view);
    localStorage.setItem('desktopCalendarView', view);
    setShowViewPopover(false);
  }, []);

  const handleDateSelect = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const handleBookingSelect = useCallback((booking: BookingItem) => {
    if (booking.type === 'blocked' || String(booking.id).startsWith('blocked-')) {
      setCalSelectedBlocked(booking);
      setCalSelectedBookingId(null);
      setCalPdfPanel(null);
      setCalPdfUrl(null);
      return;
    }

    setCalSelectedBlocked(null);
    DataStorageService.storeData('selectedBooking', booking, false);
    DataStorageService.storeData('selected-booking-id', booking.id, false);
    setCalSelectedBookingId(booking.id);
    setCalPdfPanel(null);
    setCalPdfUrl(null);
  }, []);

  const closeCalPanel = useCallback(() => {
    setCalSelectedBookingId(null);
    setCalSelectedBlocked(null);
    setBlockedEditDraft(null);
    setBlockedEditPicker(null);
    setCalPdfPanel(null);
    setCalPdfUrl(null);
    setIsSidebarEditing(false);
  }, []);

  const handleCalViewInvoice = useCallback(async (invoiceId: string) => {
    setCalPdfPanel('invoice');
    setCalPdfLoading(true);
    setCalPdfUrl(null);
    setCalPdfTitle(tr('desktop.pages.calendar.CalendarDesktop.invoice'));
    try {
      const fn = httpsCallable(getFunctions(), 'zohoInvoicing');
      const result = await fn({ action: 'pdf', invoiceId });
      const data = result.data as { pdf?: string };
      if (data?.pdf) {
        setCalPdfUrl(`data:application/pdf;base64,${data.pdf}`);
      } else {
        setCalPdfPanel(null);
      }
    } catch (err) {
      console.error('[CalendarDesktop] Error loading invoice PDF:', err);
      setCalPdfPanel(null);
    } finally {
      setCalPdfLoading(false);
    }
  }, [tr]);

  const handleCalViewSO = useCallback(async (soId: string) => {
    setCalPdfPanel('so');
    setCalPdfLoading(true);
    setCalPdfUrl(null);
    setCalPdfTitle(tr('desktop.pages.calendar.CalendarDesktop.serviceOrder'));
    try {
      const fn = httpsCallable(getFunctions(), 'zohoInvoicing');
      const result = await fn({ action: 'salesOrderPdf', salesOrderId: soId });
      const data = result.data as { pdf?: string };
      if (data?.pdf) {
        setCalPdfUrl(`data:application/pdf;base64,${data.pdf}`);
      } else {
        setCalPdfPanel(null);
      }
    } catch (err) {
      console.error('[CalendarDesktop] Error loading SO PDF:', err);
      setCalPdfPanel(null);
    } finally {
      setCalPdfLoading(false);
    }
  }, [tr]);

  const handleAddBooking = useCallback(() => {
    DataStorageService.storeData('bookingSelectedDate', currentDate.toISOString());
    setShowNewBooking(true);
    setShowAddPopover(false);
  }, [currentDate]);

  const openCreateBlockedDraft = useCallback((baseDate: Date) => {
    const next = new Date(baseDate);
    if (next.getHours() === 0 && next.getMinutes() === 0) {
      next.setHours(9, 0, 0, 0);
    } else {
      next.setMinutes(Math.round(next.getMinutes() / 15) * 15, 0, 0);
    }

    const end = new Date(next);
    end.setHours(next.getHours() + 1, next.getMinutes(), 0, 0);

    // i18n-ignore: this title is saved to the calendar, so it stays English

    const newBlocked = {
      id: 'new-blocked',
      type: 'blocked',
      title: tr('desktop.pages.calendar.CalendarDesktop.blockedTime2'),
      serviceType: 'Blocked Time',
      startTime: next,
      endTime: end,
      agendaItem: { title: tr('desktop.pages.calendar.CalendarDesktop.blockedTime2') },
    } as unknown as BookingItem;

    const defaultStaff = (currentUserEmail && staffOptions.find((option) => option.email.toLowerCase() === currentUserEmail)) || staffOptions[0];

    setCalSelectedBookingId(null);
    setCalSelectedBlocked(newBlocked);
    setBlockedEditDraft({
      eventId: '',
      scheduleId: defaultStaff?.scheduleId,
      staffId: defaultStaff?.id,
      availability: 'BUSY',
      title: '',
      date: format(next, 'yyyy-MM-dd'),
      startTime: format(next, 'HH:mm'),
      endTime: format(end, 'HH:mm'),
      allDay: false,
      notes: '',
      repeat: false,
      repeatInterval: 1,
      repeatUntil: '',
    });
    setBlockedEditPicker(null);
    setCalPdfPanel(null);
    setCalPdfUrl(null);
  }, [staffOptions, currentUserEmail, tr]);

  const handleAddBlockedTime = useCallback(() => {
    setShowAddPopover(false);
    setSlotMenu(null);
    openCreateBlockedDraft(currentDate);
  }, [currentDate, openCreateBlockedDraft]);

  const handleEditBlocked = useCallback(() => {
    if (!calSelectedBlocked || blockedActionBusy) return;

    const eventId = resolveBlockedEventId(calSelectedBlocked);
    if (!eventId) {
      window.alert(tr('desktop.pages.calendar.CalendarDesktop.unableToEditThisBlockedTime'));
      return;
    }

    const agenda = (calSelectedBlocked as unknown as Record<string, unknown>).agendaItem as Record<string, unknown> | undefined;
    const blockedAny = calSelectedBlocked as unknown as Record<string, unknown>;
    const revision = typeof agenda?.revision === 'string' ? agenda.revision : undefined;
    const scheduleId = (
      (typeof agenda?.scheduleId === 'string' && agenda.scheduleId)
      || (typeof blockedAny.scheduleId === 'string' && blockedAny.scheduleId)
      || (typeof agenda?.externalScheduleId === 'string' && agenda.externalScheduleId)
      || (typeof blockedAny.externalScheduleId === 'string' && blockedAny.externalScheduleId)
      || undefined
    );
    const staffId = staffOptions.find((option) => option.scheduleId === scheduleId)?.id;
    const transparency = typeof agenda?.transparency === 'string' ? agenda.transparency : undefined;
    const availability: 'BUSY' | 'AVAILABLE' = transparency === 'TRANSPARENT' ? 'AVAILABLE' : 'BUSY';
    const recurrenceType = typeof agenda?.recurrenceType === 'string' ? agenda.recurrenceType : undefined;
    const recurrenceRule = agenda?.recurrenceRule as { frequency?: string; interval?: number; days?: string[]; until?: string } | undefined;
    const repeat = recurrenceType === 'MASTER' && !!recurrenceRule;
    const repeatInterval = recurrenceRule?.interval || 1;
    const repeatUntil = recurrenceRule?.until ? recurrenceRule.until.slice(0, 10) : '';

    const start = toDate(calSelectedBlocked.startTime);
    const end = toDate(calSelectedBlocked.endTime);
    const title = (calSelectedBlocked.agendaItem?.title || calSelectedBlocked.title || 'Blocked Time').trim();
    const notes = String(
      agenda?.notes
      || (calSelectedBlocked as unknown as Record<string, unknown>).notes
      || ''
    ).trim();

    setBlockedEditDraft({
      eventId,
      revision,
      scheduleId,
      staffId,
      availability,
      title: title || 'Blocked Time',
      date: format(start, 'yyyy-MM-dd'),
      startTime: format(start, 'HH:mm'),
      endTime: format(end, 'HH:mm'),
      allDay: false,
      notes,
      repeat,
      repeatInterval,
      repeatUntil,
    });
    setBlockedEditPicker(null);
  }, [blockedActionBusy, calSelectedBlocked, staffOptions, tr]);

  const handleSaveBlockedEdit = useCallback(async () => {
    if (!blockedEditDraft || blockedEditBusy) return;

    setBlockedEditBusy(true);
    try {
      const safeTitle = blockedEditDraft.title.trim() || 'Blocked Time';
      const safeDate = normalizeDateInput(blockedEditDraft.date, format(new Date(), 'yyyy-MM-dd'));
      const safeStartTime = normalizeTimeInput(blockedEditDraft.startTime, '09:00');
      const safeEndTime = normalizeTimeInput(blockedEditDraft.endTime, safeStartTime);
      const startLocal = blockedEditDraft.allDay
        ? `${safeDate}T00:00:00`
        : buildLocalDateTime(safeDate, safeStartTime, '09:00');
      const endLocal = blockedEditDraft.allDay
        ? `${safeDate}T23:59:00`
        : buildLocalDateTime(safeDate, safeEndTime, safeStartTime);

      const fetchFallbackScheduleId = async (): Promise<string | null> => {
        try {
          const [staffResult, schedulesResult] = await Promise.all([
            callAdminApi('queryStaffMembers', 'bookings', {}),
            callAdminApi('querySchedules', 'bookings', {}),
          ]);

          const anyStaff = staffResult as Record<string, unknown>;
          const staffList = (Array.isArray(anyStaff.items) ? anyStaff.items : Array.isArray(anyStaff._items) ? anyStaff._items : []) as Array<Record<string, unknown>>;
          const staffResourceIds = new Set<string>();

          for (const staff of staffList) {
            const staffId = typeof staff._id === 'string' ? staff._id.trim() : '';
            const resourceId = typeof staff.resourceId === 'string' ? staff.resourceId.trim() : '';
            if (staffId) staffResourceIds.add(staffId);
            if (resourceId) staffResourceIds.add(resourceId);

            const scheduleIds = Array.isArray(staff.scheduleIds) ? staff.scheduleIds : [];
            const firstStaffSchedule = scheduleIds.find((sid) => typeof sid === 'string' && sid.trim());
            if (typeof firstStaffSchedule === 'string' && firstStaffSchedule.trim()) {
              return firstStaffSchedule.trim();
            }
          }

          const anySchedules = schedulesResult as Record<string, unknown>;
          const scheduleList = (Array.isArray(anySchedules.items) ? anySchedules.items : Array.isArray(anySchedules._items) ? anySchedules._items : []) as Array<Record<string, unknown>>;
          for (const schedule of scheduleList) {
            const scheduleId = typeof schedule._id === 'string' ? schedule._id.trim() : (typeof schedule.id === 'string' ? schedule.id.trim() : '');
            // Schedule has no `resourceId` field — `externalId` is what equals
            // the resource's id for a staff member's own schedule.
            const scheduleResourceId = typeof schedule.externalId === 'string' ? schedule.externalId.trim() : '';
            if (scheduleId && scheduleResourceId && staffResourceIds.has(scheduleResourceId)) {
              return scheduleId;
            }
          }
        } catch (staffScheduleError) {
          console.error('[CalendarDesktop] Failed to load staff schedule id:', staffScheduleError);
        }

        try {
          const hoursResult = await callAdminApi('getBusinessHours', 'businessInfo', {});
          const hoursData = hoursResult as Record<string, unknown>;
          const businessScheduleId = typeof hoursData.scheduleId === 'string' ? hoursData.scheduleId : '';
          if (businessScheduleId) return businessScheduleId;
        } catch (businessHoursError) {
          console.error('[CalendarDesktop] Failed to load business schedule id:', businessHoursError);
        }

        try {
          const result = await callAdminApi('querySchedules', 'bookings', {});
          const anyResult = result as Record<string, unknown>;
          const list = (Array.isArray(anyResult.items) ? anyResult.items : Array.isArray(anyResult._items) ? anyResult._items : []) as Array<Record<string, unknown>>;
          for (const item of list) {
            const id = typeof item._id === 'string' ? item._id : typeof item.id === 'string' ? item.id : '';
            if (id) return id;
          }
        } catch (scheduleError) {
          console.error('[CalendarDesktop] Failed to load schedules for blocked create:', scheduleError);
        }
        return null;
      };

      const eventPayload: Record<string, unknown> = {
        title: safeTitle,
        start: { localDate: startLocal },
        end: { localDate: endLocal },
        transparency: blockedEditDraft.availability === 'AVAILABLE' ? 'TRANSPARENT' : 'OPAQUE',
      };

      if (blockedEditDraft.repeat) {
        eventPayload.recurrenceRule = {
          frequency: 'WEEKLY',
          interval: blockedEditDraft.repeatInterval,
          days: [getWixWeekday(safeDate)],
          ...(blockedEditDraft.repeatUntil ? { until: { localDate: `${blockedEditDraft.repeatUntil}T23:59:00` } } : {}),
        };
      }

      console.log('[CalendarDesktop] Blocked event payload times', {
        rawDate: blockedEditDraft.date,
        rawStartTime: blockedEditDraft.startTime,
        rawEndTime: blockedEditDraft.endTime,
        normalizedDate: safeDate,
        normalizedStartTime: safeStartTime,
        normalizedEndTime: safeEndTime,
        startLocal,
        endLocal,
      });

      if (blockedEditDraft.notes.trim()) {
        eventPayload.notes = blockedEditDraft.notes.trim();
      }

      const isCreate = !blockedEditDraft.eventId.trim();
      if (isCreate) {
        const scheduleId = blockedEditDraft.scheduleId || await fetchFallbackScheduleId();
        if (!scheduleId) {
          window.alert(tr('desktop.pages.calendar.CalendarDesktop.unableToCreateBlockedTimeMissing'));
          return;
        }
        eventPayload.type = 'DEFAULT';
        eventPayload.scheduleId = scheduleId;
        const created = await callAdminApi('createEvent', 'bookings', eventPayload) as Record<string, unknown>;
        const createdInner = (created.event && typeof created.event === 'object')
          ? created.event as Record<string, unknown>
          : created;
        const createdId =
          (typeof createdInner._id === 'string' && createdInner._id)
          || (typeof createdInner.id === 'string' && createdInner.id)
          || `pending-${Date.now()}`;

        // Insert the new card directly instead of waiting for cache sync.
        const newStart = new Date(startLocal);
        const newEnd = new Date(endLocal);
        const createdStaffName = staffOptions.find((option) => option.id === blockedEditDraft.staffId)?.name || '';
        const newItem = {
          id: `blocked-${createdId}`,
          type: 'blocked',
          title: safeTitle,
          serviceType: 'Blocked Time',
          startTime: newStart,
          endTime: newEnd,
          date: safeDate,
          notes: (eventPayload.notes as string) || '',
          staff: createdStaffName,
          agendaItem: {
            _id: createdId,
            type: 'BLOCKED_TIME',
            title: safeTitle,
            notes: (eventPayload.notes as string) || '',
            revision: typeof createdInner.revision === 'string' ? createdInner.revision : undefined,
            scheduleId,
            startDate: newStart.toISOString(),
            endDate: newEnd.toISOString(),
            transparency: eventPayload.transparency as string,
            isBlocked: true,
            isAppointment: false,
            staffMember: createdStaffName ? { _id: blockedEditDraft.staffId || scheduleId, name: createdStaffName } : null,
            slot: {
              startDate: newStart.toISOString(),
              endDate: newEnd.toISOString(),
              staffMember: createdStaffName ? { _id: blockedEditDraft.staffId || scheduleId, name: createdStaffName } : null,
              resource: scheduleId ? { _id: scheduleId, type: 'SCHEDULE' } : null,
            },
          },
        } as unknown as BookingItem;

        setBookingsByDay((prev) => {
          const targetIndex = prev.findIndex((day) => isSameDay(day.date, newStart));
          if (targetIndex === -1) return prev;
          const next = [...prev];
          next[targetIndex] = {
            ...next[targetIndex],
            items: [...next[targetIndex].items, newItem].sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime()),
          };
          return next;
        });
      } else {
        if (blockedEditDraft.revision) eventPayload.revision = blockedEditDraft.revision;
        // Without this, changing the Staff picker while editing had no effect —
        // the update payload never actually carried the new schedule assignment.
        const updateScheduleId = blockedEditDraft.scheduleId || await fetchFallbackScheduleId();
        if (updateScheduleId) eventPayload.scheduleId = updateScheduleId;
        await callAdminApi('updateEvent', 'bookings', {
          id: blockedEditDraft.eventId,
          event: eventPayload,
        });

        // Optimistically move/update the existing card in place instead of a full
        // reload — same approach as the drag-to-reschedule fix.
        const updatedId = `blocked-${blockedEditDraft.eventId}`;
        const newStart = new Date(startLocal);
        const newEnd = new Date(endLocal);
        setBookingsByDay((prev) => {
          let updatedItem: BookingItem | undefined;
          for (const day of prev) {
            const found = day.items.find((item) => item.id === updatedId);
            if (found) { updatedItem = found; break; }
          }
          if (!updatedItem) return prev;

          const updatedStaffName = staffOptions.find((option) => option.id === blockedEditDraft.staffId)?.name || '';
          const merged = {
            ...updatedItem,
            startTime: newStart,
            endTime: newEnd,
            date: safeDate,
            title: safeTitle,
            notes: (eventPayload.notes as string) || '',
            staff: updatedStaffName,
            agendaItem: {
              ...(updatedItem as unknown as { agendaItem?: Record<string, unknown> }).agendaItem,
              title: safeTitle,
              notes: (eventPayload.notes as string) || '',
            },
          } as BookingItem;

          const stripped = prev.map((day) => ({
            ...day,
            items: day.items.filter((item) => item.id !== updatedId),
          }));
          const targetIndex = stripped.findIndex((day) => isSameDay(day.date, newStart));
          if (targetIndex === -1) return prev;
          stripped[targetIndex] = {
            ...stripped[targetIndex],
            items: [...stripped[targetIndex].items, merged].sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime()),
          };
          return stripped;
        });
      }

      setBlockedEditDraft(null);
      setBlockedEditPicker(null);
      closeCalPanel();
    } catch (error) {
      console.error('[CalendarDesktop] Failed to edit blocked time:', error);
      window.alert(tr('desktop.pages.calendar.CalendarDesktop.failedToSaveBlockedTime'));
    } finally {
      setBlockedEditBusy(false);
    }
  }, [blockedEditBusy, blockedEditDraft, closeCalPanel, staffOptions, tr]);

  // Part of a repeating series — Delete asks whether to remove just this
  // occurrence or the whole series (same as the mobile BlockedTimeForm).
  const selectedBlockedRecurrence = (calSelectedBlocked?.agendaItem as { recurrenceType?: string; recurringEventId?: string } | undefined);
  const isSelectedBlockedRecurring = ['MASTER', 'INSTANCE', 'EXCEPTION'].includes(selectedBlockedRecurrence?.recurrenceType ?? '');

  const performDeleteBlocked = useCallback(async (scope: 'single' | 'series' = 'single') => {
    if (!calSelectedBlocked || blockedActionBusy) return;

    const eventId = resolveBlockedEventId(calSelectedBlocked);
    if (!eventId) return;

    setBlockedActionBusy(true);
    try {
      await callAdminApi('deleteEvent', 'bookings', { id: eventId, scope });
      // Optimistically drop the card(s) instead of a full reload.
      const removedId = `blocked-${eventId}`;
      const agenda = calSelectedBlocked.agendaItem as { recurringEventId?: string } | undefined;
      const masterId = agenda?.recurringEventId || eventId;
      setBookingsByDay((prev) => prev.map((day) => ({
        ...day,
        items: day.items.filter((item) => {
          if (item.id === removedId) return false;
          if (scope !== 'series') return true;
          const itemAgenda = item.agendaItem as { recurringEventId?: string } | undefined;
          return item.id !== `blocked-${masterId}` && itemAgenda?.recurringEventId !== masterId;
        }),
      })));
      closeCalPanel();
    } catch (error) {
      console.error('[CalendarDesktop] Failed to delete blocked time:', error);
      window.alert(tr('desktop.pages.calendar.CalendarDesktop.failedToDeleteBlockedTime'));
    } finally {
      setBlockedActionBusy(false);
    }
  }, [blockedActionBusy, calSelectedBlocked, closeCalPanel, tr]);

  const handleDeleteBlocked = useCallback(() => {
    if (!calSelectedBlocked || blockedActionBusy) return;

    const eventId = resolveBlockedEventId(calSelectedBlocked);
    if (!eventId) {
      window.alert(tr('desktop.pages.calendar.CalendarDesktop.unableToDeleteThisBlockedTime'));
      return;
    }

    setShowDeleteBlockedConfirm(true);
  }, [calSelectedBlocked, blockedActionBusy, tr]);

  const handleDropBooking = useCallback(async (date: Date, startMinutes: number) => {
    if (!activeDragRef.current) return;
    const currentDrag = activeDragRef.current;
    if (currentDrag.sourceElement) {
      currentDrag.sourceElement.style.visibility = '';
    }
    const durationMs = toDate(currentDrag.booking.endTime).getTime() - toDate(currentDrag.booking.startTime).getTime();
    const newStart = new Date(date);
    newStart.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);
    const newEnd = new Date(newStart.getTime() + durationMs);
    const updated: BookingItem = {
      ...currentDrag.booking,
      startTime: newStart,
      endTime: newEnd,
      date: format(newStart, 'yyyy-MM-dd'),
    };
    const movedBookingId = currentDrag.booking.id;
    setDragVisual(null);
    setCommittedDrag(null);
    dragCommittedRef.current = false;
    activeDragRef.current = null;
    dragStartPointerRef.current = null;

    // Optimistically move the card in the visible grid immediately.
    setBookingsByDay((prev) => {
      const next = prev.map((day) => ({
        ...day,
        items: day.items.filter((item) => item.id !== movedBookingId),
      }));

      const targetIndex = next.findIndex((day) => isSameDay(day.date, date));
      if (targetIndex !== -1) {
        next[targetIndex] = {
          ...next[targetIndex],
          items: [...next[targetIndex].items, updated].sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime()),
        };
      }

      return next;
    });

    const isBlockedDrag = 'type' in currentDrag.booking && (currentDrag.booking as unknown as { type?: string }).type === 'blocked';

    try {
      if (isBlockedDrag) {
        const eventId = resolveBlockedEventId(currentDrag.booking);
        if (!eventId) throw new Error('Missing event id for blocked time drag');
        const agenda = (currentDrag.booking as unknown as Record<string, unknown>).agendaItem as Record<string, unknown> | undefined;
        const revision = typeof agenda?.revision === 'string' ? agenda.revision : undefined;
        const eventPayload: Record<string, unknown> = {
          start: { localDate: format(newStart, "yyyy-MM-dd'T'HH:mm:ss") },
          end: { localDate: format(newEnd, "yyyy-MM-dd'T'HH:mm:ss") },
          transparency: 'OPAQUE',
        };
        if (revision) eventPayload.revision = revision;
        await callAdminApi('updateEvent', 'bookings', { id: eventId, event: eventPayload });
      } else {
        const service = DataServiceFactory.getService();
        await service.bookings.updateBooking(updated as BookingRecord);
      }
    } catch (err) {
      console.error('[CalendarDesktop] Reschedule failed:', err);
      // Force a re-fetch to roll back the optimistic move.
      setRefreshTrigger(t => t + 1);
    }
  }, []);

  const resolveDropPosition = useCallback((clientX: number, clientY: number, currentDrag: { booking: BookingItem; grabOffsetMinutes: number }) => {
    const columns = timeGridRef.current?.querySelectorAll<HTMLElement>('[data-column-date]') ?? [];
    for (const element of columns) {
      const dateAttr = element.getAttribute('data-column-date');
      if (!dateAttr) continue;
      const date = parse(dateAttr, 'yyyy-MM-dd', new Date());
      const rect = element.getBoundingClientRect();
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        continue;
      }

      const rawY = clientY - rect.top;
      const durationMs = toDate(currentDrag.booking.endTime).getTime() - toDate(currentDrag.booking.startTime).getTime();
      const durationMinutes = Math.ceil(durationMs / 60000);
      const rawStart = rawY - currentDrag.grabOffsetMinutes;
      const snapped = Math.round(rawStart / 15) * 15;
      const clamped = Math.max(0, Math.min(snapped, 1440 - durationMinutes));
      return { date, startMinutes: clamped, durationMinutes };
    }
    return null;
  }, []);

  function handleBookingDragEnd() {
    if (activeDragRef.current?.sourceElement) {
      activeDragRef.current.sourceElement.style.visibility = '';
    }
    if (pointerMoveHandlerRef.current) {
      window.removeEventListener('mousemove', pointerMoveHandlerRef.current);
      pointerMoveHandlerRef.current = null;
    }
    if (pointerUpHandlerRef.current) {
      window.removeEventListener('mouseup', pointerUpHandlerRef.current);
      pointerUpHandlerRef.current = null;
    }
    setDragVisual(null);
    setCommittedDrag(null);
    dragCommittedRef.current = false;
    activeDragRef.current = null;
    dragStartPointerRef.current = null;
  }

  const handlePointerDragStart = useCallback((payload: { booking: BookingItem; renderKey: string; sourceElement: HTMLDivElement; grabOffsetMinutes: number; x: number; y: number; width: number; height: number; offsetX: number; offsetY: number; title: string; subtitle: string; color: string }) => {
    activeDragRef.current = {
      booking: payload.booking,
      renderKey: payload.renderKey,
      sourceElement: payload.sourceElement,
      grabOffsetMinutes: payload.grabOffsetMinutes,
      width: payload.width,
      height: payload.height,
      offsetX: payload.offsetX,
      offsetY: payload.offsetY,
      title: payload.title,
      subtitle: payload.subtitle,
      color: payload.color,
    };
    dragCommittedRef.current = false;
    setCommittedDrag(null);
    dragStartPointerRef.current = { x: payload.x, y: payload.y };
    setDragVisual(null);
    const handlePointerMove = (e: MouseEvent) => {
      const currentDrag = activeDragRef.current;
      if (!currentDrag) return;

      // Require left-button hold for drag; prevents sticky click-to-drag behavior.
      if ((e.buttons & 1) !== 1) {
        handleBookingDragEnd();
        return;
      }

      const start = dragStartPointerRef.current;
      const moved = start ? Math.hypot(e.clientX - start.x, e.clientY - start.y) >= 4 : true;
      if (!moved) return;

      if (!dragCommittedRef.current) {
        dragCommittedRef.current = true;
        setCommittedDrag({ renderKey: currentDrag.renderKey, booking: currentDrag.booking });
        if (currentDrag.sourceElement) {
          currentDrag.sourceElement.style.visibility = 'hidden';
        }
      }

      setDragVisual((prev) => {
        if (!prev) {
          return {
            x: e.clientX,
            y: e.clientY,
            width: currentDrag.width,
            height: currentDrag.height,
            offsetX: currentDrag.offsetX,
            offsetY: currentDrag.offsetY,
            title: currentDrag.title,
            subtitle: currentDrag.subtitle,
            color: currentDrag.color,
          };
        }

        return { ...prev, x: e.clientX, y: e.clientY };
      });

      if (!dragCommittedRef.current) {
        return;
      }

      const nextPreview = resolveDropPosition(e.clientX, e.clientY, currentDrag);
      if (!nextPreview) {
        return;
      }
    };

    const handlePointerUp = (e: MouseEvent) => {
      const currentDrag = activeDragRef.current;
      if (!currentDrag) {
        handleBookingDragEnd();
        return;
      }

      const start = dragStartPointerRef.current;
      const moved = start ? Math.hypot(e.clientX - start.x, e.clientY - start.y) >= 4 : false;
      if (moved && dragCommittedRef.current) {
        const finalDrop = resolveDropPosition(e.clientX, e.clientY, currentDrag);
        if (finalDrop) {
          void handleDropBooking(finalDrop.date, finalDrop.startMinutes);
          return;
        }
      }

      handleBookingDragEnd();
    };

    pointerMoveHandlerRef.current = handlePointerMove;
    pointerUpHandlerRef.current = handlePointerUp;
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp, { once: true });
  }, [resolveDropPosition, handleDropBooking]);

  const handleAddBookingFromSlot = useCallback((date: Date) => {
    DataStorageService.storeData('bookingSelectedDate', date.toISOString());
    setSlotMenu(null);
    setShowNewBooking(true);
  }, []);

  const handleAddBlockedFromSlot = useCallback((date: Date) => {
    setSlotMenu(null);
    openCreateBlockedDraft(date);
  }, [openCreateBlockedDraft]);

  // Storage listener for refresh triggers
  useEffect(() => {
    const unsubscribe = DataStorageService.setupStorageListener((key: string) => {
      // 'booking-refresh-trigger' used to force a full recalendar reload here —
      // removed in favor of trusting the live Firestore listener (subscribeToUpcomingBookings)
      // to pick up appointment edits incrementally, same fix as the drag-to-reschedule flow.
      if (key === 'open-booking-modal') {
        const bookingId = DataStorageService.getData<string>('open-booking-modal');
        if (bookingId) {
          DataStorageService.removeData('open-booking-modal');
          setSelectedBookingId(bookingId);
          setIsBookingModalOpen(true);
        }
      } else if (key === 'edit-booking-modal') {
        const bookingId = DataStorageService.getData<string>('edit-booking-modal');
        if (bookingId) {
          DataStorageService.removeData('edit-booking-modal');
          setSelectedBookingId(bookingId);
          setIsBookingModalOpen(true);
        }
      }
    });
    return unsubscribe;
  }, []);

  // Header title
  const headerTitle = useMemo(() => {
    switch (calendarView) {
      case 'weekly':
        return format(viewStart, 'MMMM yyyy', { locale: dfLocale() });
      case 'daily':
        return format(currentDate, 'EEEE, MMMM d, yyyy', { locale: dfLocale() });
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: dfLocale() });
      case 'schedule':
        return `${format(viewStart, 'MMM d', { locale: dfLocale() })} - ${format(viewEnd, 'MMM d, yyyy', { locale: dfLocale() })}`;
    }
  }, [calendarView, currentDate, viewStart, viewEnd]);

  const getViewLabel = (view: CalendarView) => {
    switch (view) {
      case 'weekly': return tr('desktop.pages.calendar.CalendarDesktop.weekly');
      case 'daily': return translateDynamic('Daily');
      case 'month': return tr('desktop.pages.calendar.CalendarDesktop.month');
      case 'schedule': return tr('desktop.pages.calendar.CalendarDesktop.schedule');
    }
  };

  const getViewIcon = (view: CalendarView) => {
    switch (view) {
      case 'weekly': return calendarOutline;
      case 'daily': return todayOutline;
      case 'month': return gridOutline;
      case 'schedule': return listOutline;
    }
  };

  // Process overlapping bookings using interval scheduling so non-overlapping
  // bookings share a column, minimising unnecessary width splits.
  const processBookingsForDay = useCallback((bookings: BookingItem[]): (BookingItem & { columnIndex: number; totalColumns: number })[] => {
    if (bookings.length === 0) return [];

    const sorted = [...bookings].sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime());

    // Assign each booking to the first column it fits in (greedy interval scheduling)
    const columnEnds: number[] = [];
    const assignedCol: number[] = [];

    for (const booking of sorted) {
      const bStart = toDate(booking.startTime).getTime();
      const bEnd = toDate(booking.endTime).getTime();
      let placed = false;
      for (let col = 0; col < columnEnds.length; col++) {
        if (bStart >= columnEnds[col]) {
          columnEnds[col] = bEnd;
          assignedCol.push(col);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columnEnds.push(bEnd);
        assignedCol.push(columnEnds.length - 1);
      }
    }

    // Group into connected overlap clusters (union-find over direct overlaps)
    // and give every booking in the same cluster the SAME totalColumns. Using
    // a per-booking max (only that booking's own direct overlaps) let two
    // bookings that are themselves overlapping end up with different
    // totalColumns whenever their broader overlap sets differed — since
    // width/left both derive from totalColumns, that misaligned two
    // supposedly-adjacent cards' actual DOM boxes from their painted stagger
    // position, so a click in the visually-front card's area could land on
    // the DOM box of a differently-sized neighboring card underneath.
    const parent = sorted.map((_, i) => i);
    const find = (i: number): number => {
      while (parent[i] !== i) {
        parent[i] = parent[parent[i]];
        i = parent[i];
      }
      return i;
    };
    const union = (a: number, b: number) => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[ra] = rb;
    };

    for (let i = 0; i < sorted.length; i++) {
      const iStart = toDate(sorted[i].startTime).getTime();
      const iEnd = toDate(sorted[i].endTime).getTime();
      for (let j = i + 1; j < sorted.length; j++) {
        const jStart = toDate(sorted[j].startTime).getTime();
        const jEnd = toDate(sorted[j].endTime).getTime();
        if (jStart < iEnd && jEnd > iStart) {
          union(i, j);
        }
      }
    }

    const clusterMaxCol = new Map<number, number>();
    for (let i = 0; i < sorted.length; i++) {
      const root = find(i);
      clusterMaxCol.set(root, Math.max(clusterMaxCol.get(root) ?? 0, assignedCol[i]));
    }

    return sorted.map((booking, i) => ({
      ...booking,
      columnIndex: assignedCol[i],
      totalColumns: (clusterMaxCol.get(find(i)) ?? assignedCol[i]) + 1,
    }));
  }, []);

  // Render time grid (weekly/daily)
  const renderTimeGrid = () => {
    const dates = calendarView === 'daily' ? [currentDate] : headerDates;
    const isDaily = calendarView === 'daily';
    const suppressDraggedRenderKey = committedDrag ? committedDrag.renderKey : null;
    const suppressDraggedId = committedDrag ? String(committedDrag.booking.id) : null;

    return (
      <div className={styles.timeGridContainer}>
        {/* Day headers */}
        <div className={styles.timeGridHeader}>
          <div className={styles.timeGutter} />
          {dates.map(date => (
            <div
              key={date.getTime()}
              className={`${styles.dayHeaderCol} ${isSameDay(date, new Date()) ? styles.todayHeader : ''} ${isDaily ? styles.dailyHeader : ''}`}
              onClick={() => handleDateSelect(date)}
            >
              <span className={styles.dayHeaderName}>{format(date, 'EEE', { locale: dfLocale() })}</span>
              <span className={`${styles.dayHeaderDate} ${isSameDay(date, new Date()) ? styles.todayDate : ''}`}>
                {format(date, 'd')}
              </span>
            </div>
          ))}
        </div>

        {/* Time grid body */}
        <div className={styles.timeGridBody} ref={timeGridRef}>
          <div className={styles.timeGridInner}>
            {/* Time labels */}
            <div className={styles.timeGutter}>
              {HOURS.map(hour => (
                <div key={hour} className={styles.timeLabel}>
                  <span>{formatHour(hour)}</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {dates.map(date => {
              const dayBookings = getBookingsForDate(date);
              const processed = processBookingsForDay(dayBookings);
              const isToday = isSameDay(date, new Date());

              return (
                <div
                  key={date.getTime()}
                  data-column-date={format(date, 'yyyy-MM-dd')}
                  className={`${styles.dayColumn} ${isToday ? styles.todayColumn : ''} ${isDaily ? styles.dailyColumn : ''}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('ion-card')) return;
                    if ((e.target as HTMLElement).closest(`.${styles.bookingCardWrapper}`)) return;
                    if ((e.target as HTMLElement).closest(`.${styles.bookingCard}`)) return;
                    const col = e.currentTarget as HTMLDivElement;
                    const colRect = col.getBoundingClientRect();
                    const clickY = e.clientY - colRect.top;
                    const totalMinutes = Math.round(clickY / 15) * 15;
                    const hours = Math.min(Math.floor(totalMinutes / 60), 23);
                    const minutes = Math.min(totalMinutes % 60, 59);
                    const slotDate = new Date(date);
                    slotDate.setHours(hours, minutes, 0, 0);
                    const slotCenterY = colRect.top + (hours * 60 + minutes) + 7;
                    const side: 'right' | 'left' = window.innerWidth - colRect.right >= 240 ? 'right' : 'left';
                    // store the relevant column edge; positioning uses left vs right CSS property
                    const edgeX = side === 'right' ? colRect.right : colRect.left;
                    setSlotMenu({ x: edgeX, y: slotCenterY, date: slotDate, hasTime: true, side });
                  }}
                >
                  {/* Hour grid lines */}
                  {HOURS.map(hour => (
                    <div key={hour} className={styles.hourCell}>
                      <div className={styles.halfHourLine} />
                    </div>
                  ))}

                  {/* Booking cards */}
                  {processed.map((booking, sortIndex) => {
                    const renderKey = `${booking.id}-${formatTimeToString(booking.startTime)}-${formatTimeToString(booking.endTime)}`;
                    const isSuppressedByKey = suppressDraggedRenderKey === renderKey;
                    const isSuppressedById = suppressDraggedId !== null && String(booking.id) === suppressDraggedId;
                    if (isSuppressedByKey || isSuppressedById) {
                      return null;
                    }

                    return (
                      <BookingCard
                        key={renderKey}
                        renderKey={renderKey}
                        booking={booking}
                        onSelect={handleBookingSelect}
                        columnIndex={booking.columnIndex}
                        totalColumns={booking.totalColumns}
                        sortIndex={sortIndex}
                        onPointerDragStart={handlePointerDragStart}
                        isDragging={false}
                      />
                    );
                  })}

                  {/* Slot selection highlight */}
                  {slotMenu?.hasTime && isSameDay(slotMenu.date, date) && (
                    <div
                      className={styles.slotHighlight}
                      style={{ top: `${slotMenu.date.getHours() * 60 + slotMenu.date.getMinutes()}px` }}
                    >
                      <span>{formatTo12Hour(format(slotMenu.date, 'HH:mm'))}</span>
                    </div>
                  )}

                  {/* Current time indicator */}
                  {isToday && (
                    <>
                      <IonBadge
                        className={styles.currentTimeLine}
                        style={{ '--current-time-top': `${currentTimePosition}px` } as React.CSSProperties}
                      >
                        &nbsp;
                      </IonBadge>
                      <IonBadge
                        className={styles.currentTimeDot}
                        style={{ '--current-time-top': `${currentTimePosition}px` } as React.CSSProperties}
                      >
                        &nbsp;
                      </IonBadge>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render month grid
  const renderMonthGrid = () => {
    const monthStart = startOfMonth(currentDate);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(endOfMonth(currentDate));

    const days: Date[] = [];
    let d = calStart;
    while (d <= calEnd) {
      days.push(d);
      d = addDays(d, 1);
    }

    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className={styles.monthGrid}>
        <div className={styles.monthGridHeader}>
          {weekdayNames('short').map(day => (
            <div key={day} className={styles.monthGridHeaderCell}>{day}</div>
          ))}
        </div>
        <div className={styles.monthGridBody}>
          {weeks.map((week, wi) => (
            <div key={wi} className={styles.monthGridRow}>
              {week.map(day => {
                const dayBookings = getBookingsForDate(day);
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, currentDate);
                const dateKey = format(day, 'yyyy-MM-dd');
                const isExpanded = expandedMonthCells.has(dateKey);
                const displayBookings = isExpanded ? dayBookings : dayBookings.slice(0, 4);

                return (
                  <div
                    key={day.getTime()}
                    className={`${styles.monthGridCell} ${isToday ? styles.todayCell : ''} ${!isCurrentMonth ? styles.otherMonthCell : ''} ${isExpanded ? styles.expandedCell : ''}`}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('ion-chip')) return;
                      if (isExpanded) {
                        setExpandedMonthCells(prev => {
                          const next = new Set(prev);
                          next.delete(dateKey);
                          return next;
                        });
                      } else {
                        setCurrentDate(day);
                        setCalendarView('daily');
                        localStorage.setItem('desktopCalendarView', 'daily');
                      }
                    }}
                  >
                    <span className={`${styles.monthGridDate} ${isToday ? styles.todayDate : ''}`}>
                      {day.getDate()}
                    </span>
                    <div className={styles.monthGridBookings}>
                      {displayBookings.map(booking => {
                        const isBlocked = 'type' in booking && booking.type === 'blocked';
                        const serviceColor = isBlocked ? 'var(--ion-color-medium)' : (serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)');
                        const blockedTitle = ('agendaItem' in booking && booking.agendaItem?.title) || booking.title || tr('desktop.pages.calendar.CalendarDesktop.blocked');
                        const contactName = isBlocked ? blockedTitle : (booking.contact?.name || booking.title || 'Unnamed');
                        return (
                          <IonChip
                            key={booking.id}
                            className={`${styles.monthGridBooking} ${isBlocked ? styles.monthGridBookingBlocked : ''}`}
                            style={{ '--booking-color': serviceColor } as React.CSSProperties}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookingSelect(booking);
                            }}
                          >
                            <IonLabel>{isBlocked ? contactName : `${contactName}: ${booking.serviceType}`}</IonLabel>
                          </IonChip>
                        );
                      })}
                      {!isExpanded && dayBookings.length > 4 && (
                        <IonChip
                          className={styles.monthGridMore}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedMonthCells(prev => new Set([...prev, dateKey]));
                          }}
                        >
                          <IonLabel>+{dayBookings.length - 4}{tr('desktop.pages.calendar.CalendarDesktop.more')}</IonLabel>
                        </IonChip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render schedule view
  const renderScheduleView = () => {
    return (
      <div className={styles.scheduleView}>
        {headerDates.map(date => {
          const dayBookings = getBookingsForDate(date);
          const isToday = isSameDay(date, new Date());

          return (
            <div key={date.getTime()} className={styles.scheduleDay}>
              <div className={`${styles.scheduleDayHeader} ${isToday ? styles.todayScheduleHeader : ''}`}>
                <span className={styles.scheduleDayName}>{format(date, 'EEEE', { locale: dfLocale() })}</span>
                <span className={styles.scheduleDayDate}>{format(date, 'MMM d', { locale: dfLocale() })}</span>
              </div>
              {dayBookings.length === 0 ? (
                <div className={styles.scheduleEmpty}>{tr('desktop.pages.calendar.CalendarDesktop.noBookings')}</div>
              ) : (
                <IonList className={styles.scheduleItems}>
                  {dayBookings
                    .sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime())
                    .map(booking => (
                      <ScheduleItem key={booking.id} booking={booking} onSelect={handleBookingSelect} />
                    ))}
                </IonList>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className={styles.desktopToolbar}>
          <IonButtons slot="start">
            <IonTitle size="small" className={styles.pageTitle}>{tr('desktop.pages.calendar.CalendarDesktop.bookingCalendar')}</IonTitle>
          </IonButtons>

          <div className={styles.headerCenter} slot="end">
            <IonButton fill="clear" shape='round' className={styles.todayButton} onClick={goToToday}>
              <TodayCalendarIcon className={styles.todayButtonIcon} />{tr('desktop.pages.calendar.CalendarDesktop.today')}
            </IonButton>

            <div className={styles.navButtons}>
              <IonButton fill="clear" size="small" onClick={navigatePrev}>
                <IonIcon icon={chevronBackOutline} />
              </IonButton>
              <span className={styles.headerTitle}>{headerTitle}</span>
              <IonButton fill="clear" size="small" onClick={navigateNext}>
                <IonIcon icon={chevronForwardOutline} />
              </IonButton>
            </div>

            <IonButton
              fill="clear"
              shape='round'
              id="desktop-view-trigger"
              className={styles.viewSelector}
              onClick={() => setShowViewPopover(true)}
            >
              <IonIcon icon={getViewIcon(calendarView)} slot="start" />
              {getViewLabel(calendarView)}
            </IonButton>

            <IonPopover
              isOpen={showViewPopover}
              onDidDismiss={() => setShowViewPopover(false)}
              trigger="desktop-view-trigger"
              side="bottom"
              alignment="end"
              dismissOnSelect
              className={styles.viewPopover}
              showBackdrop={false}
            >
              <IonList>
                <IonRadioGroup value={calendarView} onIonChange={(e) => handleViewChange(e.detail.value as CalendarView)}>
                  {(['weekly', 'daily', 'month', 'schedule'] as CalendarView[]).map(view => (
                    <IonItem key={view} button detail={false} lines="none">
                      <IonIcon slot="start" icon={getViewIcon(view)} color={calendarView === view ? 'primary' : undefined} />
                      <IonLabel color={calendarView === view ? 'primary' : undefined}>{getViewLabel(view)}</IonLabel>
                      <IonRadio slot="end" value={view} />
                    </IonItem>
                  ))}
                </IonRadioGroup>
              </IonList>
            </IonPopover>

            <IonButton
              fill="outline"
              shape='round'
              className={styles.addButton}
              id="add-button"
              onClick={() => setShowAddPopover(true)}
            >{tr('desktop.pages.calendar.CalendarDesktop.add')}
              <IonIcon icon={chevronDownOutline} slot="end" />
            </IonButton>

            <IonPopover
              trigger="add-button"
              isOpen={showAddPopover}
              onDidDismiss={() => setShowAddPopover(false)}
              className={styles.viewPopover}
            >
              <IonList>
                <IonItem button detail={false} onClick={handleAddBooking}>
                  <IonIcon icon={calendarOutline} slot="start" />
                  <IonLabel>{tr('desktop.pages.calendar.CalendarDesktop.appointment')}</IonLabel>
                </IonItem>
                <IonItem button detail={false} onClick={handleAddBlockedTime}>
                  <IonIcon icon={calendarOutline} slot="start" />
                  <IonLabel>{tr('desktop.pages.calendar.CalendarDesktop.blockedTime')}</IonLabel>
                </IonItem>
              </IonList>
            </IonPopover>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className={`${styles.desktopContainer} ${isSidebarEditing ? styles.desktopContainerEditing : ''}`}>
          {/* Left sidebar */}
          <div className={styles.leftSidebar}>
            <MiniCalendar
              currentMonth={miniCalMonth}
              selectedDate={currentDate}
              onDateSelect={handleDateSelect}
              onMonthChange={setMiniCalMonth}
              bookingsByDate={bookingsByDate}
            />

            {/* Upcoming sessions / sidebar info */}
            <div className={styles.sidebarSection}>
              <h4 className={styles.sidebarTitle}>{tr('desktop.pages.calendar.CalendarDesktop.upcoming')}</h4>
              {(() => {
                const todayBookings = getBookingsForDate(new Date());
                const upcoming = todayBookings
                  .filter(b => toDate(b.startTime) > new Date())
                  .sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime());

                if (upcoming.length === 0) {
                  return <p className={styles.sidebarEmpty}>{tr('desktop.pages.calendar.CalendarDesktop.noUpcomingBookingsToday')}</p>;
                }

                return upcoming.slice(0, 5).map(booking => {
                  const serviceColor = serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';
                  const isBlocked = booking.type === 'blocked';
                  const upcomingTitle = isBlocked
                    ? translateDynamic(booking.agendaItem?.title || 'Blocked Time')
                    : booking.serviceType;
                  return (
                    <IonItem
                      key={booking.id}
                      className={styles.upcomingItem}
                      button
                      detail={false}
                      lines="none"
                      onClick={() => handleBookingSelect(booking)}
                    >
                      <IonBadge slot="start" className={styles.upcomingColor} style={{ '--service-color': serviceColor } as React.CSSProperties}>&nbsp;</IonBadge>
                      <IonLabel>
                        <div className={styles.upcomingDetails}>
                          <span className={styles.upcomingTitle}>{upcomingTitle}</span>
                          <span className={styles.upcomingTime}>
                            {formatTo12Hour(formatTimeToString(booking.startTime))}
                          </span>
                          <span className={styles.upcomingContact}>
                            {booking.contact?.name || booking.title || tr('desktop.pages.calendar.CalendarDesktop.unnamed')}
                          </span>
                        </div>
                      </IonLabel>
                    </IonItem>
                  );
                });
              })()}
            </div>
          </div>

          {/* Main content area */}
          <div className={styles.mainContent}>
            {isLoading ? (
              <div className={styles.loadingState}>
                <IonText color="medium">{tr('desktop.pages.calendar.CalendarDesktop.loadingBookings')}</IonText>
              </div>
            ) : (
              <>
                {(calendarView === 'weekly' || calendarView === 'daily') && renderTimeGrid()}
                {calendarView === 'month' && renderMonthGrid()}
                {calendarView === 'schedule' && renderScheduleView()}
              </>
            )}
          </div>

          {/* PDF panel — appears to the left of booking panel when invoice/SO is open */}
          {calSelectedBookingId && calPdfPanel && (
            <div className={styles.calPdfPanel}>
              <div className={styles.calPdfPanelHeader}>
                <span className={styles.calPdfPanelTitle}>{calPdfTitle}</span>
                <button
                  className={styles.calPanelCloseBtn}
                  onClick={() => { setCalPdfPanel(null); setCalPdfUrl(null); }}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <PdfViewerNative dataUrl={calPdfUrl} isLoading={calPdfLoading} />
            </div>
          )}

          {/* Booking details side panel */}
          {(calSelectedBookingId || calSelectedBlocked) && (
            <div className={styles.calBookingPanel}>
              <div className={styles.calPanelHeader}>
                <button className={styles.calPanelCloseBtn} onClick={closeCalPanel}>
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div
                className={styles.calBookingPanelContent}
                style={blockedEditPicker ? { overflow: 'visible' } : undefined}
              >
                {calSelectedBlocked ? (
                  <div
                    className={bookingDetailsStyles.embScroll}
                    style={blockedEditPicker ? { overflow: 'visible' } : undefined}
                  >
                    {(() => {
                      const blockedAny = calSelectedBlocked as unknown as Record<string, unknown>;
                      const blockedTitle = calSelectedBlocked.agendaItem?.title || calSelectedBlocked.title || 'Blocked Time';
                      const contactName = String(blockedTitle);
                      const blockedId = String(blockedAny.id ?? calSelectedBlocked.id ?? '');
                      const blockedStatus = String(blockedAny.status ?? 'blocked');
                      const startDate = toDate(calSelectedBlocked.startTime);
                      const endDate = toDate(calSelectedBlocked.endTime);
                      const dateLabel = format(startDate, 'EEE, MMM d', { locale: dfLocale() });
                      const timeLabel = `${formatTo12Hour(formatTimeToString(startDate))} - ${formatTo12Hour(formatTimeToString(endDate))}`;
                      const notes = String(blockedAny.notes || '').trim();

                      if (blockedEditDraft) {
                        return (
                          <div className={bookingDetailsStyles.bookingContent}>
                            <div className={bookingDetailsStyles.hero}>
                              <div className={bookingDetailsStyles.heroTop}>
                                <span className={bookingDetailsStyles.heroName}>{blockedEditDraft.eventId ? tr('desktop.pages.calendar.CalendarDesktop.editBlockedTime') : tr('desktop.pages.calendar.CalendarDesktop.newBlockedTime')}</span>
                              </div>
                              <div className={bookingDetailsStyles.heroSub}>
                                <span className={bookingDetailsStyles.jobNum}>#{blockedId || 'n/a'}</span>
                                <span className={styles.blockedEditHint}>
                                  {format(new Date(`${blockedEditDraft.date}T00:00:00`), 'MMM d, yyyy', { locale: dfLocale() })}
                                </span>
                              </div>
                            </div>

                            <div className={bookingDetailsStyles.section}>
                              <div className={bookingDetailsStyles.sectionTitle}>{tr('desktop.pages.calendar.CalendarDesktop.schedule')}</div>
                              <div ref={blockedEditFieldsRef} className={bookingDetailsStyles.editBlock}>
                                <div className={styles.blockedEditFieldRow}>
                                  <div className={bookingDetailsStyles.editField}>
                                    <span>{tr('desktop.pages.calendar.CalendarDesktop.date')}</span>
                                    <div className={styles.blockedPickerWrap}>
                                      <button
                                        type="button"
                                        className={styles.blockedPickerTrigger}
                                        onClick={() => setBlockedEditPicker((p) => p === 'date' ? null : 'date')}
                                      >
                                        <span className={styles.blockedPickerValue}>{format(new Date(`${blockedEditDraft.date}T00:00:00`), 'EEE, MMM d, yyyy', { locale: dfLocale() })}</span>
                                        <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                      </button>
                                      {blockedEditPicker === 'date' && (
                                        <>
                                          <div
                                            className={styles.blockedPickerBackdrop}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                          />
                                          <div className={styles.blockedCalendarDropdown}>
                                            <MiniCalendarPicker
                                              desktop
                                              value={new Date(`${blockedEditDraft.date}T00:00:00`)}
                                              onSelect={(nextDate) => {
                                                const nextIso = format(nextDate, 'yyyy-MM-dd');
                                                setBlockedEditDraft((d) => d ? { ...d, date: nextIso } : d);
                                                setBlockedEditPicker(null);
                                              }}
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className={styles.blockedEditToggleField}>
                                    <label className={styles.blockedCheckboxLabel}>
                                      <input
                                        type="checkbox"
                                        className={styles.blockedCheckboxInput}
                                        checked={blockedEditDraft.repeat}
                                        onChange={(e) => {
                                          setBlockedEditPicker(null);
                                          setBlockedEditDraft((d) => d ? { ...d, repeat: e.target.checked } : d);
                                        }}
                                      />
                                      <span className={styles.blockedCheckboxBox}>
                                        <IonIcon icon={checkmark} className={styles.blockedCheckboxIcon} />
                                      </span>
                                      <span>{tr('desktop.pages.calendar.CalendarDesktop.repeat')}</span>
                                    </label>
                                  </div>
                                </div>

                                {blockedEditDraft.repeat && (
                                  <div className={styles.blockedEditFieldRow}>
                                    <div className={bookingDetailsStyles.editField}>
                                      <span>{tr('desktop.pages.calendar.CalendarDesktop.every')}</span>
                                      <div className={styles.blockedPickerWrap}>
                                        <button
                                          type="button"
                                          className={styles.blockedPickerTrigger}
                                          onClick={() => setBlockedEditPicker((p) => p === 'repeatInterval' ? null : 'repeatInterval')}
                                        >
                                          <span className={styles.blockedPickerValue}>{tr('desktop.pages.calendar.CalendarDesktop.week', { repeatInterval: blockedEditDraft.repeatInterval })}{blockedEditDraft.repeatInterval > 1 ? 's' : ''}</span>
                                          <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                        </button>
                                        {blockedEditPicker === 'repeatInterval' && (
                                          <>
                                            <div
                                              className={styles.blockedPickerBackdrop}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                              onTouchStart={(e) => {
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                            />
                                            <div className={`${styles.blockedTimeDropdown} ${styles.blockedPickerDropdownRight}`}>
                                              <div className={styles.blockedOptionsList}>
                                                {[1, 2, 3, 4].map((n) => (
                                                  <button
                                                    key={n}
                                                    type="button"
                                                    className={`${styles.blockedOptionItem} ${blockedEditDraft.repeatInterval === n ? styles.blockedOptionItemActive : ''}`}
                                                    onClick={() => {
                                                      setBlockedEditDraft((d) => d ? { ...d, repeatInterval: n } : d);
                                                      setBlockedEditPicker(null);
                                                    }}
                                                  >{tr('desktop.pages.calendar.CalendarDesktop.everyWeek', { n })}{n > 1 ? 's' : ''}
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div className={bookingDetailsStyles.editField}>
                                      <span>{tr('desktop.pages.calendar.CalendarDesktop.ends')}</span>
                                      <div className={styles.blockedPickerWrap}>
                                        <button
                                          type="button"
                                          className={styles.blockedPickerTrigger}
                                          onClick={() => setBlockedEditPicker((p) => p === 'repeatUntil' ? null : 'repeatUntil')}
                                        >
                                          <span className={styles.blockedPickerValue}>{blockedEditDraft.repeatUntil ? format(new Date(`${blockedEditDraft.repeatUntil}T00:00:00`), 'MMM d, yyyy', { locale: dfLocale() }) : tr('desktop.pages.calendar.CalendarDesktop.never')}</span>
                                          <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                        </button>
                                        {blockedEditPicker === 'repeatUntil' && (
                                          <>
                                            <div
                                              className={styles.blockedPickerBackdrop}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                              onTouchStart={(e) => {
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                            />
                                            <div className={`${styles.blockedCalendarDropdown} ${styles.blockedPickerDropdownRight}`}>
                                              <div className={styles.blockedOptionsList}>
                                                <button
                                                  type="button"
                                                  className={`${styles.blockedOptionItem} ${!blockedEditDraft.repeatUntil ? styles.blockedOptionItemActive : ''}`}
                                                  onClick={() => {
                                                    setBlockedEditDraft((d) => d ? { ...d, repeatUntil: '' } : d);
                                                    setBlockedEditPicker(null);
                                                  }}
                                                >{tr('desktop.pages.calendar.CalendarDesktop.never')}
                                                </button>
                                              </div>
                                              <MiniCalendarPicker
                                                desktop
                                                value={blockedEditDraft.repeatUntil ? new Date(`${blockedEditDraft.repeatUntil}T00:00:00`) : new Date(`${blockedEditDraft.date}T00:00:00`)}
                                                onSelect={(nextDate) => {
                                                  const nextIso = format(nextDate, 'yyyy-MM-dd');
                                                  setBlockedEditDraft((d) => d ? { ...d, repeatUntil: nextIso } : d);
                                                  setBlockedEditPicker(null);
                                                }}
                                              />
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className={styles.blockedEditToggleRow}>
                                  <label className={styles.blockedCheckboxLabel}>
                                    <input
                                      type="checkbox"
                                      className={styles.blockedCheckboxInput}
                                      checked={blockedEditDraft.allDay}
                                      onChange={(e) => {
                                        setBlockedEditPicker(null);
                                        setBlockedEditDraft((d) => d ? { ...d, allDay: e.target.checked } : d);
                                      }}
                                    />
                                    <span className={styles.blockedCheckboxBox}>
                                      <IonIcon icon={checkmark} className={styles.blockedCheckboxIcon} />
                                    </span>
                                    <span>{tr('desktop.pages.calendar.CalendarDesktop.allDay')}</span>
                                  </label>
                                </div>

                                {!blockedEditDraft.allDay && (
                                  <div className={styles.blockedEditFieldRow}>
                                    <div className={bookingDetailsStyles.editField}>
                                      <span>{tr('desktop.pages.calendar.CalendarDesktop.startTime')}</span>
                                      <div className={styles.blockedPickerWrap}>
                                        <button
                                          type="button"
                                          className={styles.blockedPickerTrigger}
                                          onClick={() => setBlockedEditPicker((p) => p === 'start' ? null : 'start')}
                                        >
                                          <span className={styles.blockedPickerValue}>{formatTo12Hour(blockedEditDraft.startTime)}</span>
                                          <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                        </button>
                                        {blockedEditPicker === 'start' && (
                                          <>
                                            <div
                                              className={styles.blockedPickerBackdrop}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                              onTouchStart={(e) => {
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                            />
                                            <div className={styles.blockedTimeDropdown}>
                                              <GcalTimePicker
                                                desktop
                                                value={blockedEditDraft.startTime || '09:00'}
                                                onSelect={(nextTime) => {
                                                  setBlockedEditDraft((d) => d ? { ...d, startTime: normalizeTimeInput(nextTime || d.startTime, d.startTime) } : d);
                                                  setBlockedEditPicker(null);
                                                }}
                                                onClose={() => setBlockedEditPicker(null)}
                                              />
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div className={bookingDetailsStyles.editField}>
                                      <span>{tr('desktop.pages.calendar.CalendarDesktop.endTime')}</span>
                                      <div className={styles.blockedPickerWrap}>
                                        <button
                                          type="button"
                                          className={styles.blockedPickerTrigger}
                                          onClick={() => setBlockedEditPicker((p) => p === 'end' ? null : 'end')}
                                        >
                                          <span className={styles.blockedPickerValue}>{formatTo12Hour(blockedEditDraft.endTime)}</span>
                                          <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                        </button>
                                        {blockedEditPicker === 'end' && (
                                          <>
                                            <div
                                              className={styles.blockedPickerBackdrop}
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                              onTouchStart={(e) => {
                                                e.stopPropagation();
                                                setBlockedEditPicker(null);
                                              }}
                                            />
                                            <div className={`${styles.blockedTimeDropdown} ${styles.blockedPickerDropdownRight}`}>
                                              <GcalTimePicker
                                                desktop
                                                value={blockedEditDraft.endTime || blockedEditDraft.startTime || '10:00'}
                                                onSelect={(nextTime) => {
                                                  setBlockedEditDraft((d) => d ? { ...d, endTime: normalizeTimeInput(nextTime || d.endTime, d.endTime) } : d);
                                                  setBlockedEditPicker(null);
                                                }}
                                                onClose={() => setBlockedEditPicker(null)}
                                              />
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className={styles.blockedEditFieldRow}>
                                  <div className={bookingDetailsStyles.editField}>
                                    <span>{tr('desktop.pages.calendar.CalendarDesktop.staff')}</span>
                                    <div className={styles.blockedPickerWrap}>
                                      <button
                                        type="button"
                                        className={styles.blockedPickerTrigger}
                                        onClick={() => setBlockedEditPicker((p) => p === 'staff' ? null : 'staff')}
                                      >
                                        <span className={styles.blockedPickerValue}>{staffOptions.find((option) => option.id === blockedEditDraft.staffId)?.name || (staffOptionsLoading ? tr('desktop.pages.calendar.CalendarDesktop.loadingStaff') : staffOptions.length === 0 ? tr('desktop.pages.calendar.CalendarDesktop.noStaffFound') : tr('desktop.pages.calendar.CalendarDesktop.selectStaff'))}</span>
                                        <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                      </button>
                                      {blockedEditPicker === 'staff' && (
                                        <>
                                          <div
                                            className={styles.blockedPickerBackdrop}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                          />
                                          <div className={styles.blockedTimeDropdown}>
                                            <div className={styles.blockedOptionsList}>
                                              {staffOptions.map((option) => (
                                                <button
                                                  key={option.id}
                                                  type="button"
                                                  className={`${styles.blockedOptionItem} ${blockedEditDraft.staffId === option.id ? styles.blockedOptionItemActive : ''}`}
                                                  onClick={() => {
                                                    setBlockedEditDraft((d) => d ? { ...d, staffId: option.id, scheduleId: option.scheduleId } : d);
                                                    setBlockedEditPicker(null);
                                                  }}
                                                >
                                                  {option.name}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className={bookingDetailsStyles.editField}>
                                    <span>{tr('desktop.pages.calendar.CalendarDesktop.availability')}</span>
                                    <div className={styles.blockedPickerWrap}>
                                      <button
                                        type="button"
                                        className={styles.blockedPickerTrigger}
                                        onClick={() => setBlockedEditPicker((p) => p === 'availability' ? null : 'availability')}
                                      >
                                        <span className={styles.blockedPickerValue}>{blockedEditDraft.availability === 'AVAILABLE' ? tr('desktop.pages.calendar.CalendarDesktop.available') : tr('desktop.pages.calendar.CalendarDesktop.busy')}</span>
                                        <IonIcon icon={chevronForwardOutline} className={styles.blockedPickerChevron} />
                                      </button>
                                      {blockedEditPicker === 'availability' && (
                                        <>
                                          <div
                                            className={styles.blockedPickerBackdrop}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                              setBlockedEditPicker(null);
                                            }}
                                          />
                                          <div className={`${styles.blockedTimeDropdown} ${styles.blockedPickerDropdownRight}`}>
                                            <div className={styles.blockedOptionsList}>
                                              {(['BUSY', 'AVAILABLE'] as const).map((value) => (
                                                <button
                                                  key={value}
                                                  type="button"
                                                  className={`${styles.blockedOptionItem} ${blockedEditDraft.availability === value ? styles.blockedOptionItemActive : ''}`}
                                                  onClick={() => {
                                                    setBlockedEditDraft((d) => d ? { ...d, availability: value } : d);
                                                    setBlockedEditPicker(null);
                                                  }}
                                                >
                                                  {value === 'BUSY' ? tr('desktop.pages.calendar.CalendarDesktop.busy') : tr('desktop.pages.calendar.CalendarDesktop.available')}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className={bookingDetailsStyles.section}>
                              <div className={bookingDetailsStyles.sectionTitle}>{tr('desktop.pages.calendar.CalendarDesktop.details')}</div>
                              <div className={bookingDetailsStyles.editBlock}>
                                <label className={bookingDetailsStyles.editField}>
                                  <span>{tr('desktop.pages.calendar.CalendarDesktop.title')}</span>
                                  <input
                                    className={styles.blockedDesktopInput}
                                    value={blockedEditDraft.title}
                                    placeholder={tr('desktop.pages.calendar.CalendarDesktop.eGPrivateEvent')}
                                    onChange={(e) => setBlockedEditDraft((d) => d ? { ...d, title: e.target.value } : d)}
                                  />
                                </label>
                                <label className={bookingDetailsStyles.editField}>
                                  <span>{tr('desktop.pages.calendar.CalendarDesktop.note')}</span>
                                  <textarea
                                    className={styles.blockedDesktopTextarea}
                                    value={blockedEditDraft.notes}
                                    rows={3}
                                    onChange={(e) => setBlockedEditDraft((d) => d ? { ...d, notes: e.target.value || '' } : d)}
                                  />
                                </label>
                                <div className={bookingDetailsStyles.editActions}>
                                  <button
                                    type="button"
                                    className={styles.blockedEditCancelBtn}
                                    onClick={() => setBlockedEditDraft(null)}
                                    disabled={blockedEditBusy}
                                  >{tr('desktop.pages.calendar.CalendarDesktop.cancel')}
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.blockedEditSaveBtn}
                                    onClick={handleSaveBlockedEdit}
                                    disabled={blockedEditBusy}
                                  >{tr('desktop.pages.calendar.CalendarDesktop.save')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className={bookingDetailsStyles.bookingContent}>
                          <div className={bookingDetailsStyles.hero}>
                            <div className={bookingDetailsStyles.heroTop}>
                              <span className={bookingDetailsStyles.heroName}>{contactName}</span>
                            </div>
                            <div className={bookingDetailsStyles.heroSub}>
                              <span className={bookingDetailsStyles.jobNum}>#{blockedId || 'n/a'}</span>
                              <span className={`${bookingDetailsStyles.chip} ${bookingDetailsStyles.chip_primary}`}>
                                <IonIcon icon={alertCircleOutline} />
                                {blockedStatus}
                              </span>
                              <div className={styles.blockedActions}>
                                <button
                                  type="button"
                                  className={styles.blockedActionBtn}
                                  onClick={handleEditBlocked}
                                  disabled={blockedActionBusy}
                                >
                                  <IonIcon icon={createOutline} />{tr('desktop.pages.calendar.CalendarDesktop.edit')}
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.blockedActionBtn} ${styles.blockedActionBtnDanger}`}
                                  onClick={handleDeleteBlocked}
                                  disabled={blockedActionBusy}
                                >
                                  <IonIcon icon={trashOutline} />{tr('desktop.pages.calendar.CalendarDesktop.delete')}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className={bookingDetailsStyles.section}>
                            <div className={bookingDetailsStyles.sectionTitle}>{tr('desktop.pages.calendar.CalendarDesktop.schedule')}</div>
                            <div className={bookingDetailsStyles.fieldRows}>
                              <div className={bookingDetailsStyles.fieldRow}>
                                <IonIcon icon={calendarOutline} className={bookingDetailsStyles.fieldIcon} />
                                <span className={bookingDetailsStyles.fieldValue}>{dateLabel}</span>
                              </div>
                              <div className={bookingDetailsStyles.fieldRow}>
                                <IonIcon icon={timeOutline} className={bookingDetailsStyles.fieldIcon} />
                                <span className={bookingDetailsStyles.fieldValue}>{timeLabel}</span>
                              </div>
                              <div className={bookingDetailsStyles.fieldRow}>
                                <IonIcon icon={personOutline} className={bookingDetailsStyles.fieldIcon} />
                                <span className={bookingDetailsStyles.fieldValue}>{tr('desktop.pages.calendar.CalendarDesktop.staff2')} {String(calSelectedBlocked.staff || blockedAny.staffName || 'n/a')}</span>
                              </div>
                            </div>
                          </div>

                          <div className={bookingDetailsStyles.section}>
                            <div className={bookingDetailsStyles.sectionTitle}>{tr('desktop.pages.calendar.CalendarDesktop.notes')}</div>
                            <p className={bookingDetailsStyles.noteBlock}>{notes || 'n/a'}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <BookingDetailsDesktop
                    embedded
                    bookingId={calSelectedBookingId || undefined}
                    onCancel={closeCalPanel}
                    onViewInvoice={handleCalViewInvoice}
                    onViewSO={handleCalViewSO}
                    onEditModeChange={setIsSidebarEditing}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </IonContent>

      <Booking
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        bookingId={selectedBookingId || undefined}
      />


      {showNewBooking && (
        <div className={styles.createOverlay}>
          <CreateBookingDesktop onClose={() => { setShowNewBooking(false); setRefreshTrigger(t => t + 1); }} />
        </div>
      )}

      {dragVisual && committedDrag && createPortal(
        <BookingCard
          key={`dragging-${String(committedDrag.booking.id)}`}
          renderKey={committedDrag.renderKey}
          booking={committedDrag.booking as ExtendedBookingItem}
          onSelect={handleBookingSelect}
          columnIndex={0}
          totalColumns={1}
          isDragging
          floatingStyle={{
            position: 'fixed',
            left: `${dragVisual.x - dragVisual.offsetX}px`,
            top: `${dragVisual.y - dragVisual.offsetY}px`,
            width: `${dragVisual.width}px`,
            height: `${dragVisual.height}px`,
          } as React.CSSProperties}
        />,
        document.body
      )}

      {slotMenu && createPortal(
        <>
          <div className={styles.slotMenuBackdrop} onClick={() => setSlotMenu(null)} />
          {/* Rotated-square glass arrow: wrapper clips right half, inner has full backdrop-filter */}
          <div
            className={`${styles.slotMenuArrowWrap} ${slotMenu.side === 'left' ? styles.slotMenuArrowWrapLeft : ''}`}
            style={{
              left: slotMenu.side === 'right' ? slotMenu.x : undefined,
              right: slotMenu.side === 'left' ? window.innerWidth - slotMenu.x : undefined,
              top: Math.max(0, Math.min(slotMenu.y - 9, window.innerHeight - 18)),
            }}
          >
            <div className={styles.slotMenuArrowInner} />
          </div>
          <div
            className={`${styles.slotContextMenu} ${slotMenu.side === 'left' ? styles.slotContextMenuLeft : ''}`}
            style={
              slotMenu.side === 'right'
                ? { left: slotMenu.x + 7, top: Math.max(8, Math.min(slotMenu.y - 48, window.innerHeight - 104)) }
                : { right: window.innerWidth - slotMenu.x + 7, top: Math.max(8, Math.min(slotMenu.y - 48, window.innerHeight - 104)) }
            }
          >
            <button className={styles.slotMenuItem} onClick={() => handleAddBookingFromSlot(slotMenu.date)}>{tr('desktop.pages.calendar.CalendarDesktop.appointment')}
            </button>
            <button className={styles.slotMenuItem} onClick={() => handleAddBlockedFromSlot(slotMenu.date)}>{tr('desktop.pages.calendar.CalendarDesktop.blockedTime')}
            </button>
          </div>
        </>,
        document.body
      )}

      <IonAlert
        isOpen={showDeleteBlockedConfirm}
        onDidDismiss={() => setShowDeleteBlockedConfirm(false)}
        header={tr('desktop.pages.calendar.CalendarDesktop.deleteBlockedTime')}
        message={isSelectedBlockedRecurring
          ? tr('desktop.pages.calendar.CalendarDesktop.thisBlockedTimeRepeatsDeleteOnly')
          : tr('desktop.pages.calendar.CalendarDesktop.areYouSureYouWantTo')}
        cssClass="logout-alert"
        buttons={isSelectedBlockedRecurring ? [
          { text: tr('desktop.pages.calendar.CalendarDesktop.cancel'), role: 'cancel', handler: () => setShowDeleteBlockedConfirm(false) },
          { text: tr('desktop.pages.calendar.CalendarDesktop.thisEventOnly'), handler: () => { setShowDeleteBlockedConfirm(false); void performDeleteBlocked('single'); } },
          { text: tr('desktop.pages.calendar.CalendarDesktop.allEventsInSeries'), handler: () => { setShowDeleteBlockedConfirm(false); void performDeleteBlocked('series'); } },
        ] : [
          { text: tr('desktop.pages.calendar.CalendarDesktop.cancel'), role: 'cancel', handler: () => setShowDeleteBlockedConfirm(false) },
          { text: tr('desktop.pages.calendar.CalendarDesktop.delete'), handler: () => { setShowDeleteBlockedConfirm(false); void performDeleteBlocked('single'); } },
        ]}
      />
    </IonPage>
  );
};

export default CalendarDesktop;
